<template>
  <div class="darts-calculator" id="play-dashboard">
    <div v-if="!started" class="flex gap-4 flex-col">
      <div class="flex flex-col gap-4">
        <h2 class="text-2xl font-bold border-b-1 border-white pb-4">Pick your starting score</h2>
        <div class="flex flex-row gap-[10px] border-b-1 border-white pb-4 starting-score">
          <ButtonToggle
            class="py-4 px-8"
            v-for="score in [301, 501, 701]"
            :key="score"
            :active="startingScore === score"
            @click="startingScore = score"
          >
            {{ score }}
          </ButtonToggle>
        </div>
        <p>{{ gameDescriptions[startingScore] }}</p>
      </div>
      <div class="flex flex-col gap-8 border-b-1 border-white pb-4">
        <h2 class="text-2xl font-bold border-b-1 border-white pb-4">Players and Game Options</h2>

        <div class="flex flex-row gap-[10px] no-players">
          <ButtonToggle
            class="py-1 px-3"
            v-for="number in [1, 2, 3, 4, 5, 6, 7, 8]"
            :key="number"
            :active="numberOfPlayers === number"
            @click="numberOfPlayers = number"
          >
            {{ number }}
          </ButtonToggle>
        </div>

        <div class="flex flex-col gap-4">
          <input
            v-for="number in numberOfPlayers"
            :key="number"
            type="text"
            v-model="playerNames[number - 1]"
            :placeholder="`Player ${number}`"
          />
        </div>

        <div class="flex flex-col gap-4 game-options">
          <label class="custom-container"
            ><input
              type="radio"
              id="double"
              value="1"
              name="game-out"
              v-model="gameOut"
              checked="checked"
            /><span class="checkmark"></span>Double Out<br /><span class="desc"
              >Must checkout with a double.</span
            ></label
          >
          <label class="custom-container"
            ><input type="radio" id="deadeasy" value="2" name="game-out" v-model="gameOut" /><span
              class="checkmark"
            ></span
            >Dead Easy Out<br /><span class="desc"
              >Player can checkout with a single (or double or treble if you wanna show off).</span
            ></label
          >
        </div>

        <div class="flex flex-col gap-4 mb-4">
          <label>Number of Legs</label>
          <NumberNudger :max="5" :min="1" v-model="numberOfLegs"></NumberNudger>
        </div>
      </div>
      <AccentButton class="mx-auto my-4" tag="button" @click="startGame(false)"
        >Let's throw</AccentButton
      >
    </div>

    <div v-else class="flex flex-col gap-4">
      <div class="relative">
        <div class="header">
          <div class="flex gap-2 items-start mb-5">
            <button class="back-btn" @click="resetGame()"></button>
            <div class="heading">
              <h3 class="center">{{ startingScore }}</h3>
            </div>
          </div>
        </div>
        <div class="flex gap-2">
          <div
            @click="switchTab('board')"
            :class="
              tab === 'keypad' ? 'bg-primary-dark text-[#BFC3CB]' : 'bg-primary-mid text-white'
            "
            class="flex-1 flex justify-center items-center cursor-pointer rounded-tl-xl rounded-tr-xl text-2xl font-bold py-2"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              class="mr-2"
              :class="tab === 'board' ? 'text-white' : 'text-[#BFC3CB]'"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.99999 18C7.755 18 6.585 17.7636 5.49 17.2908C4.395 16.818 3.4425 16.1769 2.6325 15.3675C1.8225 14.5581 1.1814 13.6056 0.709201 12.51C0.237001 11.4144 0.000601139 10.2444 1.13924e-06 9C-0.00059886 7.7556 0.235801 6.5856 0.709201 5.49C1.1826 4.3944 1.8237 3.4419 2.6325 2.6325C3.4413 1.8231 4.3938 1.182 5.49 0.7092C6.5862 0.2364 7.7562 0 8.99999 0C10.2438 0 11.4138 0.2364 12.51 0.7092C13.6062 1.182 14.5587 1.8231 15.3675 2.6325C16.1763 3.4419 16.8177 4.3944 17.2917 5.49C17.7657 6.5856 18.0018 7.7556 18 9C17.9982 10.2444 17.7618 11.4144 17.2908 12.51C16.8198 13.6056 16.1787 14.5581 15.3675 15.3675C14.5563 16.1769 13.6038 16.8183 12.51 17.2917C11.4162 17.7651 10.2462 18.0012 8.99999 18ZM8.99999 16.2C11.01 16.2 12.7125 15.5025 14.1075 14.1075C15.5025 12.7125 16.2 11.01 16.2 9C16.2 6.99 15.5025 5.2875 14.1075 3.8925C12.7125 2.4975 11.01 1.8 8.99999 1.8C6.99 1.8 5.2875 2.4975 3.8925 3.8925C2.4975 5.2875 1.8 6.99 1.8 9C1.8 11.01 2.4975 12.7125 3.8925 14.1075C5.2875 15.5025 6.99 16.2 8.99999 16.2ZM8.99999 14.4C7.5 14.4 6.225 13.875 5.175 12.825C4.125 11.775 3.6 10.5 3.6 9C3.6 7.5 4.125 6.225 5.175 5.175C6.225 4.125 7.5 3.6 8.99999 3.6C10.5 3.6 11.775 4.125 12.825 5.175C13.875 6.225 14.4 7.5 14.4 9C14.4 10.5 13.875 11.775 12.825 12.825C11.775 13.875 10.5 14.4 8.99999 14.4ZM8.99999 12.6C9.98999 12.6 10.8375 12.2475 11.5425 11.5425C12.2475 10.8375 12.6 9.99 12.6 9C12.6 8.01 12.2475 7.1625 11.5425 6.4575C10.8375 5.7525 9.98999 5.4 8.99999 5.4C8.01 5.4 7.1625 5.7525 6.4575 6.4575C5.7525 7.1625 5.4 8.01 5.4 9C5.4 9.99 5.7525 10.8375 6.4575 11.5425C7.1625 12.2475 8.01 12.6 8.99999 12.6ZM8.99999 10.8C8.505 10.8 8.0814 10.6239 7.7292 10.2717C7.377 9.9195 7.2006 9.4956 7.2 9C7.1994 8.5044 7.3758 8.0808 7.7292 7.7292C8.0826 7.3776 8.5062 7.2012 8.99999 7.2C9.49379 7.1988 9.91769 7.3752 10.2717 7.7292C10.6257 8.0832 10.8018 8.5068 10.8 9C10.7982 9.4932 10.6221 9.9171 10.2717 10.2717C9.92129 10.6263 9.49739 10.8024 8.99999 10.8Z"
                fill="currentColor"
              />
            </svg>
            Dartboard
          </div>
          <div
            @click="switchTab('keypad')"
            :class="
              tab === 'board' ? 'bg-primary-dark text-[#BFC3CB]' : 'bg-primary-mid text-white'
            "
            class="flex-1 flex justify-center items-center cursor-pointer rounded-tl-xl rounded-tr-xl text-2xl font-bold py-2"
          >
            <svg
              width="12"
              height="18"
              viewBox="0 0 12 18"
              fill="none"
              class="mr-2"
              :class="tab === 'keypad' ? 'text-white' : 'text-[#BFC3CB]'"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.71429 0H10.2857C10.7404 0 11.1764 0.189642 11.4979 0.527208C11.8194 0.864773 12 1.32261 12 1.8V16.2C12 16.6774 11.8194 17.1352 11.4979 17.4728C11.1764 17.8104 10.7404 18 10.2857 18H1.71429C1.25963 18 0.823593 17.8104 0.502103 17.4728C0.180612 17.1352 0 16.6774 0 16.2V1.8C0 1.32261 0.180612 0.864773 0.502103 0.527208C0.823593 0.189642 1.25963 0 1.71429 0ZM1.71429 1.8V5.4H10.2857V1.8H1.71429ZM1.71429 7.2V9H3.42857V7.2H1.71429ZM5.14286 7.2V9H6.85714V7.2H5.14286ZM8.57143 7.2V9H10.2857V7.2H8.57143ZM1.71429 10.8V12.6H3.42857V10.8H1.71429ZM5.14286 10.8V12.6H6.85714V10.8H5.14286ZM8.57143 10.8V12.6H10.2857V10.8H8.57143ZM1.71429 14.4V16.2H3.42857V14.4H1.71429ZM5.14286 14.4V16.2H6.85714V14.4H5.14286ZM8.57143 14.4V16.2H10.2857V14.4H8.57143Z"
                fill="currentColor"
              />
            </svg>

            Keypad
          </div>
        </div>

        <section
          class="flex flex-col gap-8 bg-primary-mid py-8 px-2 rounded-b-xl"
          v-if="tab === 'board'"
        >
          <h3 class="text-base text-center px-2">
            Tap where each dart hits on the virtual board, or type in your score using the keypad.
            First player to zero wins the leg.
          </h3>

          <svg
            width="100%"
            height="100%"
            viewBox="0 0 2000 2000"
            fill="none"
            version="1.1"
            id="svg197"
            sodipodi:docname="dartboard.svg"
            inkscape:version="1.4.2 (f4327f4, 2025-05-13)"
            xmlns:inkscape="http://www.inkscape.org/namespaces/inkscape"
            xmlns:sodipodi="http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:svg="http://www.w3.org/2000/svg"
          >
            <defs id="defs197" />

            <path
              d="M1987.81 843.54C1971.22 737.93 1938.1 637.81 1891.18 545.91C1843.25 452.02 1780.92 366.71 1707.1 292.89C1633.28 219.07 1547.96 156.74 1454.08 108.81C1362.18 61.89 1262.06 28.78 1156.45 12.19C1105.47 4.18 1053.22 0 1000 0C946.78 0 894.52 4.18 843.55 12.19C737.94 28.78 637.82 61.89 545.92 108.81C452.03 156.74 366.72 219.07 292.9 292.89C219.08 366.71 156.75 452.03 108.82 545.91C61.9003 637.81 28.7903 737.93 12.2003 843.54C4.19025 894.52 0.0102539 946.77 0.0102539 999.99C0.0102539 1053.21 4.19025 1105.47 12.2003 1156.45C28.7903 1262.06 61.9103 1362.18 108.83 1454.08C156.76 1547.97 219.09 1633.28 292.91 1707.1C366.73 1780.92 452.05 1843.25 545.93 1891.18C637.83 1938.1 737.95 1971.21 843.56 1987.8C894.54 1995.81 946.79 1999.99 1000.01 1999.99C1053.23 1999.99 1105.49 1995.81 1156.46 1987.8C1262.07 1971.21 1362.19 1938.1 1454.09 1891.18C1547.98 1843.25 1633.29 1780.92 1707.11 1707.1C1780.93 1633.28 1843.26 1547.96 1891.19 1454.08C1938.11 1362.18 1971.22 1262.06 1987.81 1156.45C1995.82 1105.47 2000 1053.22 2000 1000C2000 946.78 1995.82 894.52 1987.81 843.54ZM1748.6 1381.43C1708.34 1460.3 1655.98 1531.96 1593.97 1593.97C1531.96 1655.98 1460.3 1708.34 1381.43 1748.6C1304.23 1788.01 1220.13 1815.83 1131.42 1829.76C1088.6 1836.49 1044.71 1840 1000 1840C955.29 1840 911.4 1836.49 868.58 1829.76C779.87 1815.82 695.77 1788.01 618.57 1748.6C539.7 1708.34 468.04 1655.98 406.03 1593.97C344.02 1531.96 291.66 1460.3 251.4 1381.43C211.99 1304.23 184.17 1220.13 170.23 1131.42C163.5 1088.6 159.99 1044.71 159.99 1000C159.99 955.29 163.5 911.4 170.23 868.58C184.17 779.87 211.98 695.77 251.39 618.57C291.65 539.7 344.01 468.04 406.02 406.03C468.03 344.02 539.69 291.66 618.56 251.4C695.76 211.99 779.86 184.17 868.57 170.24C911.39 163.51 955.28 160 999.99 160C1044.7 160 1088.59 163.51 1131.41 170.24C1220.12 184.18 1304.22 211.99 1381.42 251.4C1460.28 291.66 1531.95 344.02 1593.96 406.03C1655.97 468.04 1708.33 539.7 1748.59 618.57C1788 695.77 1815.82 779.87 1829.76 868.58C1836.49 911.4 1840 955.29 1840 1000C1840 1044.71 1836.49 1088.6 1829.76 1131.42C1815.82 1220.13 1788.01 1304.23 1748.6 1381.43Z"
              fill="black"
              id="path1"
            />
            <path
              d="M518.76 1245.21L308.43 1352.38C322.77 1380.46 338.84 1407.71 356.49 1433.83C384.26 1474.93 416.15 1513.59 451.28 1548.72L618.16 1381.84C578.3 1341.98 544.64 1295.91 518.75 1245.21H518.76Z"
              fill="#FFFCE3"
              id="path2"
              data-segment="16"
              @click="handleSegmentClick"
            />
            <path
              d="M754.8 1481.24L647.63 1691.57C664.14 1700.01 680.94 1707.84 697.97 1715.04C756.17 1739.66 816.59 1756.87 878.59 1766.59L915.52 1533.42C858.49 1524.46 804.43 1506.58 754.8 1481.24Z"
              fill="#FFFCE3"
              id="path3"
              data-segment="19"
              @click="handleSegmentClick"
            />
            <path
              d="M1000 1540C971.26 1540 943.04 1537.75 915.52 1533.42L878.59 1766.59C918.46 1772.84 958.99 1776 1000.01 1776C1041.03 1776 1081.55 1772.84 1121.43 1766.59L1084.5 1533.42C1056.97 1537.75 1028.76 1540 1000.01 1540H1000Z"
              fill="black"
              id="path4"
              data-segment="3"
              @click="handleSegmentClick"
            />
            <path
              d="M618.16 1381.84L451.28 1548.72C486.41 1583.85 525.07 1615.74 566.17 1643.51C592.29 1661.16 619.54 1677.23 647.62 1691.57L754.79 1481.24C704.09 1455.36 658.02 1421.7 618.16 1381.84Z"
              fill="black"
              id="path5"
              data-segment="7"
              @click="handleSegmentClick"
            />
            <path
              d="M1481.24 1245.21L1691.57 1352.38C1700.01 1335.86 1707.84 1319.07 1715.04 1302.04C1739.66 1243.84 1756.87 1183.42 1766.59 1121.42L1533.42 1084.49C1524.46 1141.52 1506.58 1195.58 1481.24 1245.21Z"
              fill="black"
              id="path6"
              data-segment="10"
              @click="handleSegmentClick"
            />
            <path
              d="M466.58 1084.49L233.41 1121.42C243.13 1183.42 260.34 1243.84 284.96 1302.04C292.16 1319.06 300 1335.86 308.43 1352.38L518.76 1245.21C493.42 1195.58 475.54 1141.52 466.58 1084.49Z"
              fill="black"
              id="path7"
              data-segment="8"
              @click="handleSegmentClick"
            />
            <path
              d="M1540 1000C1540 1028.74 1537.75 1056.96 1533.42 1084.49L1766.59 1121.42C1772.84 1081.55 1776 1041.02 1776 1000C1776 958.98 1772.84 918.46 1766.59 878.58L1533.42 915.51C1537.75 943.04 1540 971.25 1540 1000Z"
              fill="#FFFCE3"
              id="path8"
              data-segment="6"
              @click="handleSegmentClick"
            />
            <path
              d="M1381.84 1381.84L1548.72 1548.72C1583.85 1513.59 1615.75 1474.93 1643.51 1433.83C1661.16 1407.71 1677.23 1380.46 1691.57 1352.38L1481.24 1245.21C1455.36 1295.91 1421.7 1341.98 1381.84 1381.84Z"
              fill="#FFFCE3"
              id="path9"
              data-segment="15"
              @click="handleSegmentClick"
            />
            <path
              d="M1245.21 1481.24L1352.38 1691.57C1380.46 1677.23 1407.71 1661.15 1433.83 1643.51C1474.93 1615.74 1513.59 1583.85 1548.72 1548.72L1381.84 1381.84C1341.98 1421.7 1295.91 1455.36 1245.21 1481.24Z"
              fill="black"
              id="path10"
              data-segment="2"
              @click="handleSegmentClick"
            />
            <path
              d="M1084.49 1533.42L1121.42 1766.59C1183.42 1756.87 1243.84 1739.66 1302.04 1715.04C1319.06 1707.84 1335.86 1700 1352.38 1691.57L1245.21 1481.24C1195.58 1506.58 1141.52 1524.46 1084.49 1533.42Z"
              fill="#FFFCE3"
              id="path11"
              data-segment="17"
              @click="handleSegmentClick"
            />
            <path
              d="M1245.2 518.76L1352.37 308.43C1335.86 299.99 1319.06 292.16 1302.03 284.96C1243.83 260.34 1183.41 243.13 1121.41 233.41L1084.48 466.58C1141.51 475.54 1195.57 493.42 1245.2 518.76Z"
              fill="#FFFCE3"
              id="path12"
              data-segment="1"
              @click="handleSegmentClick"
            />
            <path
              d="M1381.84 618.16L1548.72 451.28C1513.59 416.15 1474.93 384.26 1433.83 356.49C1407.71 338.84 1380.46 322.77 1352.38 308.43L1245.21 518.76C1295.91 544.64 1341.98 578.3 1381.84 618.16Z"
              fill="black"
              id="path13"
              data-segment="18"
              @click="handleSegmentClick"
            />
            <path
              d="M1691.57 647.63L1481.24 754.8C1506.58 804.43 1524.46 858.49 1533.42 915.52L1766.59 878.59C1756.87 816.59 1739.66 756.17 1715.04 697.97C1707.84 680.95 1700 664.15 1691.57 647.63Z"
              fill="black"
              id="path14"
              data-segment="13"
              @click="handleSegmentClick"
            />
            <path
              d="M1000 460C1028.74 460 1056.96 462.25 1084.48 466.58L1121.41 233.41C1081.54 227.16 1041.01 224 999.99 224C958.97 224 918.45 227.16 878.57 233.41L915.5 466.58C943.03 462.25 971.24 460 999.99 460H1000Z"
              fill="black"
              id="path15"
              data-segment="20"
              @click="handleSegmentClick"
            />
            <path
              d="M1481.24 754.79L1691.57 647.62C1677.23 619.54 1661.16 592.29 1643.51 566.17C1615.74 525.07 1583.85 486.41 1548.72 451.28L1381.84 618.16C1421.7 658.02 1455.36 704.09 1481.25 754.79H1481.24Z"
              fill="#FFFCE3"
              id="path16"
              data-segment="4"
              @click="handleSegmentClick"
            />
            <path
              d="M754.79 518.76L647.62 308.43C619.54 322.77 592.29 338.85 566.17 356.49C525.07 384.26 486.41 416.15 451.28 451.28L618.16 618.16C658.02 578.3 704.09 544.64 754.79 518.76Z"
              fill="black"
              id="path17"
              data-segment="12"
              @click="handleSegmentClick"
            />
            <path
              d="M915.51 466.58L878.58 233.41C816.58 243.13 756.16 260.34 697.96 284.96C680.94 292.16 664.14 300 647.62 308.43L754.79 518.76C804.42 493.42 858.48 475.54 915.51 466.58Z"
              fill="#FFFCE3"
              id="path18"
              data-segment="5"
              @click="handleSegmentClick"
            />
            <path
              d="M518.76 754.79L308.43 647.62C299.99 664.13 292.16 680.93 284.96 697.96C260.34 756.16 243.13 816.58 233.41 878.58L466.58 915.51C475.54 858.48 493.42 804.42 518.76 754.79Z"
              fill="black"
              id="path19"
              data-segment="14"
              @click="handleSegmentClick"
            />
            <path
              d="M618.16 618.16L451.28 451.28C416.15 486.41 384.25 525.07 356.49 566.17C338.84 592.29 322.77 619.54 308.43 647.62L518.76 754.79C544.64 704.09 578.3 658.02 618.16 618.16Z"
              fill="#FFFCE3"
              id="path20"
              data-segment="9"
              @click="handleSegmentClick"
            />
            <path
              d="M460 1000C460 971.26 462.25 943.04 466.58 915.51L233.41 878.58C227.16 918.45 224 958.98 224 1000C224 1041.02 227.16 1081.54 233.41 1121.42L466.58 1084.49C462.25 1056.96 460 1028.75 460 1000Z"
              fill="#FFFCE3"
              id="path21"
              data-segment="11"
              @click="handleSegmentClick"
            />
            <path
              d="M1798.2 1126.42L1766.59 1121.41C1756.87 1183.41 1739.66 1243.83 1715.04 1302.03C1707.84 1319.05 1700 1335.85 1691.57 1352.37L1720.11 1366.91L1748.6 1381.42C1788.01 1304.22 1815.83 1220.12 1829.76 1131.41L1798.2 1126.41V1126.42Z"
              fill="#FF0000"
              id="path22"
              data-segment="D10"
              @click="handleSegmentClick"
            />
            <path
              d="M1126.42 1798.2L1121.41 1766.59C1081.53 1772.84 1041.01 1776 999.99 1776C958.97 1776 918.45 1772.84 878.57 1766.59L873.56 1798.2L868.56 1829.76C911.38 1836.49 955.27 1840 999.98 1840C1044.69 1840 1088.58 1836.49 1131.4 1829.76L1126.4 1798.2H1126.42Z"
              fill="#FF0000"
              id="path23"
              data-segment="D3"
              @click="handleSegmentClick"
            />
            <path
              d="M284.96 1302.03C260.34 1243.83 243.13 1183.41 233.41 1121.41L201.8 1126.42L170.24 1131.42C184.18 1220.13 211.99 1304.23 251.41 1381.43L279.9 1366.92L308.44 1352.38C300 1335.86 292.17 1319.07 284.97 1302.04L284.96 1302.03Z"
              fill="#FF0000"
              id="path24"
              data-segment="D8"
              @click="handleSegmentClick"
            />
            <path
              d="M566.18 1643.51C525.08 1615.74 486.42 1583.85 451.29 1548.72L428.66 1571.35L406.03 1593.98C468.04 1655.99 539.7 1708.35 618.57 1748.61L633.08 1720.12L647.62 1691.58C619.54 1677.24 592.29 1661.16 566.17 1643.52L566.18 1643.51Z"
              fill="#FF0000"
              id="path25"
              data-segment="D7"
              @click="handleSegmentClick"
            />
            <path
              d="M451.29 1548.71C416.16 1513.58 384.26 1474.92 356.5 1433.82C338.85 1407.7 322.78 1380.45 308.44 1352.37L279.9 1366.91L251.41 1381.42C291.67 1460.28 344.03 1531.95 406.04 1593.96L428.67 1571.33L451.3 1548.7L451.29 1548.71Z"
              fill="#006602"
              id="path26"
              data-segment="D16"
              @click="handleSegmentClick"
            />
            <path
              d="M279.89 633.08L251.4 618.57C211.99 695.77 184.17 779.87 170.24 868.58L201.8 873.58L233.41 878.59C243.13 816.59 260.34 756.17 284.96 697.97C292.16 680.95 300 664.15 308.43 647.63L279.89 633.09V633.08Z"
              fill="#FF0000"
              id="path27"
              data-segment="D14"
              @click="handleSegmentClick"
            />
            <path
              d="M224 1000C224 958.99 227.16 918.46 233.41 878.58L201.8 873.57L170.24 868.57C163.51 911.39 160 955.28 160 999.99C160 1044.7 163.51 1088.59 170.24 1131.41L201.8 1126.41L233.41 1121.4C227.16 1081.52 224 1041 224 999.98V1000Z"
              fill="#006602"
              id="path28"
              data-segment="D11"
              @click="handleSegmentClick"
            />
            <path
              d="M1720.11 1366.92L1691.57 1352.38C1677.23 1380.46 1661.15 1407.71 1643.51 1433.83C1615.74 1474.93 1583.85 1513.59 1548.72 1548.72L1571.35 1571.35L1593.98 1593.98C1655.99 1531.97 1708.35 1460.31 1748.61 1381.44L1720.12 1366.93L1720.11 1366.92Z"
              fill="#006602"
              id="path29"
              data-segment="D15"
              @click="handleSegmentClick"
            />
            <path
              d="M1571.34 1571.34L1548.71 1548.71C1513.58 1583.84 1474.92 1615.73 1433.82 1643.5C1407.7 1661.15 1380.45 1677.22 1352.37 1691.56L1366.91 1720.1L1381.42 1748.59C1460.29 1708.33 1531.95 1655.97 1593.96 1593.96L1571.33 1571.33L1571.34 1571.34Z"
              fill="#FF0000"
              id="path30"
              data-segment="D2"
              @click="handleSegmentClick"
            />
            <path
              d="M697.97 1715.04C680.95 1707.84 664.15 1700 647.63 1691.57L633.09 1720.11L618.58 1748.6C695.78 1788.01 779.88 1815.83 868.59 1829.76L873.59 1798.2L878.6 1766.59C816.6 1756.87 756.18 1739.66 697.98 1715.04H697.97Z"
              fill="#006602"
              id="path31"
              data-segment="D19"
              @click="handleSegmentClick"
            />
            <path
              d="M1829.76 868.58L1798.2 873.58L1766.59 878.59C1772.84 918.47 1776 958.99 1776 1000.01C1776 1041.03 1772.84 1081.55 1766.59 1121.43L1798.2 1126.44L1829.76 1131.44C1836.49 1088.62 1840 1044.73 1840 1000.02C1840 955.31 1836.49 911.42 1829.76 868.6V868.58Z"
              fill="#006602"
              id="path32"
              data-segment="D6"
              @click="handleSegmentClick"
            />
            <path
              d="M1366.92 1720.11L1352.38 1691.57C1335.87 1700.01 1319.07 1707.84 1302.04 1715.04C1243.84 1739.66 1183.42 1756.87 1121.42 1766.59L1126.43 1798.2L1131.43 1829.76C1220.14 1815.82 1304.24 1788.01 1381.44 1748.6L1366.93 1720.11H1366.92Z"
              fill="#006602"
              id="path33"
              data-segment="D17"
              @click="handleSegmentClick"
            />
            <path
              d="M1131.42 170.24L1126.42 201.8L1121.41 233.41C1183.41 243.13 1243.83 260.34 1302.03 284.96C1319.05 292.16 1335.85 300 1352.37 308.43L1366.91 279.89L1381.42 251.4C1304.22 211.99 1220.12 184.17 1131.41 170.24H1131.42Z"
              fill="#006602"
              id="path34"
              data-segment="D1"
              @click="handleSegmentClick"
            />
            <path
              d="M428.66 428.66L406.03 406.03C344.02 468.04 291.66 539.7 251.4 618.57L279.89 633.08L308.43 647.62C322.77 619.54 338.84 592.29 356.49 566.17C384.26 525.07 416.15 486.41 451.28 451.28L428.65 428.65L428.66 428.66Z"
              fill="#006602"
              id="path35"
              data-segment="D9"
              @click="handleSegmentClick"
            />
            <path
              d="M1381.43 251.4L1366.92 279.89L1352.38 308.43C1380.46 322.77 1407.71 338.85 1433.83 356.49C1474.93 384.26 1513.59 416.15 1548.72 451.28L1571.35 428.65L1593.98 406.02C1531.97 344.01 1460.31 291.65 1381.44 251.39L1381.43 251.4Z"
              fill="#FF0000"
              id="path36"
              data-segment="D18"
              @click="handleSegmentClick"
            />
            <path
              d="M1748.6 618.57L1720.11 633.08L1691.57 647.62C1700.01 664.14 1707.84 680.93 1715.04 697.96C1739.66 756.16 1756.87 816.58 1766.59 878.58L1798.2 873.57L1829.76 868.57C1815.82 779.86 1788.01 695.76 1748.59 618.56L1748.6 618.57Z"
              fill="#FF0000"
              id="path37"
              data-segment="D13"
              @click="handleSegmentClick"
            />
            <path
              d="M1593.97 406.03L1571.34 428.66L1548.71 451.29C1583.84 486.42 1615.74 525.08 1643.5 566.18C1661.15 592.3 1677.22 619.55 1691.56 647.63L1720.1 633.09L1748.59 618.58C1708.33 539.71 1655.97 468.05 1593.96 406.04L1593.97 406.03Z"
              fill="#006602"
              id="path38"
              data-segment="D4"
              @click="handleSegmentClick"
            />
            <path
              d="M633.08 279.89L618.57 251.4C539.7 291.66 468.04 344.02 406.03 406.03L428.66 428.66L451.29 451.29C486.42 416.16 525.08 384.27 566.18 356.5C592.3 338.85 619.55 322.78 647.63 308.44L633.09 279.9L633.08 279.89Z"
              fill="#FF0000"
              id="path39"
              data-segment="D12"
              @click="handleSegmentClick"
            />
            <path
              d="M873.58 201.8L868.58 170.24C779.87 184.18 695.77 211.99 618.57 251.4L633.08 279.89L647.62 308.43C664.13 299.99 680.93 292.16 697.96 284.96C756.16 260.34 816.58 243.13 878.58 233.41L873.57 201.8H873.58Z"
              fill="#006602"
              id="path40"
              data-segment="D5"
              @click="handleSegmentClick"
            />
            <path
              d="m 1000,160 c -44.71,0 -88.6,3.51 -131.42,10.24 l 5,31.56 5.01,31.61 c 39.88,-6.25 80.4,-9.41 121.42,-9.41 41.02,0 81.54,3.16 121.42,9.41 l 5.01,-31.61 5,-31.56 C 1088.62,163.51 1044.73,160 1000.02,160 Z"
              fill="#ff0000"
              id="path41"
              style="display: inline"
              data-segment="D20"
              @click="handleSegmentClick"
            />
            <path
              d="M1074.25 1074.25L1336.59 1336.59C1372.22 1300.96 1401.61 1260.56 1424.24 1216.16L1093.58 1047.68C1088.55 1057.54 1082 1066.5 1074.25 1074.25Z"
              fill="#FFFCE3"
              id="path42"
              data-segment="15"
              @click="handleSegmentClick"
            />
            <path
              d="M1047.68 1093.57L1216.16 1424.23C1260.55 1401.6 1300.95 1372.21 1336.59 1336.58L1074.25 1074.24C1066.5 1081.99 1057.54 1088.54 1047.68 1093.57Z"
              fill="black"
              id="path43"
              data-segment="2"
              @click="handleSegmentClick"
            />
            <path
              d="M952.32 1093.57L783.84 1424.23C793.94 1429.38 804.24 1434.17 814.74 1438.62C850.43 1453.72 887.49 1464.27 925.52 1470.23L983.57 1103.72C972.48 1101.98 961.97 1098.5 952.32 1093.58V1093.57Z"
              fill="#FFFCE3"
              id="path44"
              data-segment="19"
              @click="handleSegmentClick"
            />
            <path
              d="M1074.48 529.77L1016.43 896.28C1027.52 898.02 1038.03 901.5 1047.68 906.42L1216.16 575.76C1206.06 570.61 1195.76 565.82 1185.26 561.37C1149.57 546.27 1112.51 535.72 1074.48 529.76V529.77Z"
              fill="#FFFCE3"
              id="path45"
              data-segment="1"
              @click="handleSegmentClick"
            />
            <path
              d="M1093.57 1047.68L1424.23 1216.16C1429.38 1206.06 1434.17 1195.76 1438.62 1185.26C1453.72 1149.57 1464.27 1112.51 1470.23 1074.48L1103.72 1016.43C1101.98 1027.52 1098.5 1038.03 1093.58 1047.68H1093.57Z"
              fill="black"
              id="path46"
              data-segment="10"
              @click="handleSegmentClick"
            />
            <path
              d="M1000 1105C994.41 1105 988.93 1104.56 983.57 1103.72L925.52 1470.23C949.98 1474.06 974.84 1476 1000 1476C1025.16 1476 1050.02 1474.06 1074.48 1470.23L1016.43 1103.72C1011.08 1104.56 1005.59 1105 1000 1105Z"
              fill="black"
              id="path47"
              data-segment="3"
              @click="handleSegmentClick"
            />
            <path
              d="M1105 1000C1105 1005.59 1104.56 1011.07 1103.72 1016.43L1470.23 1074.48C1474.06 1050.02 1476 1025.16 1476 1000C1476 974.84 1474.06 949.98 1470.23 925.52L1103.72 983.57C1104.56 988.92 1105 994.41 1105 1000Z"
              fill="#FFFCE3"
              id="path48"
              data-segment="6"
              @click="handleSegmentClick"
            />
            <path
              d="M1074.25 925.75L1336.59 663.41C1300.96 627.78 1260.56 598.39 1216.16 575.76L1047.68 906.42C1057.54 911.45 1066.5 918 1074.25 925.75Z"
              fill="black"
              id="path49"
              data-segment="18"
              @click="handleSegmentClick"
            />
            <path
              d="M1093.57 952.32L1424.23 783.84C1401.6 739.45 1372.21 699.05 1336.58 663.42L1074.24 925.76C1081.99 933.51 1088.54 942.47 1093.57 952.33V952.32Z"
              fill="#FFFCE3"
              id="path50"
              data-segment="4"
              @click="handleSegmentClick"
            />
            <path
              d="M1103.72 983.57L1470.23 925.52C1464.27 887.49 1453.71 850.43 1438.62 814.74C1434.18 804.24 1429.38 793.94 1424.23 783.84L1093.57 952.32C1098.5 961.97 1101.97 972.48 1103.71 983.57H1103.72Z"
              fill="black"
              id="path51"
              data-segment="13"
              @click="handleSegmentClick"
            />
            <path
              d="M1016.43 1103.72L1074.48 1470.23C1112.51 1464.27 1149.57 1453.71 1185.26 1438.62C1195.76 1434.18 1206.06 1429.38 1216.16 1424.23L1047.68 1093.57C1038.03 1098.5 1027.52 1101.97 1016.43 1103.71V1103.72Z"
              fill="#FFFCE3"
              id="path52"
              data-segment="17"
              @click="handleSegmentClick"
            />
            <path
              d="M952.32 906.43L783.84 575.77C739.45 598.4 699.05 627.79 663.41 663.42L925.75 925.76C933.5 918.01 942.46 911.46 952.32 906.43Z"
              fill="black"
              id="path53"
              data-segment="12"
              @click="handleSegmentClick"
            />
            <path
              d="M983.57 896.28L925.52 529.77C887.49 535.73 850.43 546.29 814.74 561.38C804.24 565.82 793.94 570.62 783.84 575.77L952.32 906.43C961.97 901.5 972.48 898.03 983.57 896.29V896.28Z"
              fill="#FFFCE3"
              id="path54"
              data-segment="5"
              @click="handleSegmentClick"
            />
            <path
              d="M925.75 925.75L663.41 663.41C627.78 699.04 598.39 739.44 575.76 783.84L906.42 952.32C911.45 942.46 918 933.5 925.75 925.75Z"
              fill="#FFFCE3"
              id="path55"
              data-segment="9"
              @click="handleSegmentClick"
            />
            <path
              d="M1000 895C1005.59 895 1011.07 895.44 1016.43 896.28L1074.48 529.77C1050.02 525.94 1025.16 524 1000 524C974.84 524 949.98 525.94 925.52 529.77L983.57 896.28C988.92 895.44 994.41 895 1000 895Z"
              fill="black"
              id="path56"
              data-segment="20"
              @click="handleSegmentClick"
            />
            <path
              d="M906.43 952.32L575.77 783.84C570.62 793.94 565.83 804.24 561.38 814.74C546.28 850.43 535.73 887.49 529.77 925.52L896.28 983.57C898.02 972.48 901.5 961.97 906.42 952.32H906.43Z"
              fill="black"
              id="path57"
              data-segment="14"
              @click="handleSegmentClick"
            />
            <path
              d="M896.28 1016.43L529.77 1074.48C535.73 1112.51 546.29 1149.57 561.38 1185.26C565.82 1195.76 570.62 1206.06 575.77 1216.16L906.43 1047.68C901.5 1038.03 898.03 1027.52 896.29 1016.43H896.28Z"
              fill="black"
              id="path58"
              data-segment="8"
              @click="handleSegmentClick"
            />
            <path
              d="M906.43 1047.68L575.77 1216.16C598.4 1260.55 627.79 1300.95 663.42 1336.58L925.76 1074.24C918.01 1066.49 911.47 1057.53 906.43 1047.67V1047.68Z"
              fill="#FFFCE3"
              id="path59"
              data-segment="16"
              @click="handleSegmentClick"
            />
            <path
              d="M895 1000C895 994.41 895.44 988.93 896.28 983.57L529.77 925.52C525.94 949.98 524 974.84 524 1000C524 1025.16 525.94 1050.02 529.77 1074.48L896.28 1016.43C895.44 1011.08 895 1005.59 895 1000Z"
              fill="#FFFCE3"
              id="path60"
              data-segment="11"
              @click="handleSegmentClick"
            />
            <path
              d="M925.75 1074.25L663.41 1336.59C699.04 1372.22 739.44 1401.61 783.84 1424.24L952.32 1093.58C942.46 1088.55 933.5 1082 925.75 1074.25Z"
              fill="black"
              id="path61"
              data-segment="7"
              @click="handleSegmentClick"
            />
            <path
              d="M663.42 1336.58L640.79 1359.21L618.16 1381.84C658.02 1421.7 704.09 1455.36 754.79 1481.24L769.31 1452.74L783.84 1424.23C739.45 1401.6 699.05 1372.21 663.41 1336.58H663.42Z"
              fill="#FF0000"
              id="path62"
              data-segment="T7"
              @click="handleSegmentClick"
            />
            <path
              d="M1230.69 1452.75L1216.16 1424.24C1206.06 1429.39 1195.76 1434.18 1185.26 1438.63C1149.57 1453.73 1112.51 1464.28 1074.48 1470.24L1079.49 1501.85L1084.49 1533.43C1141.52 1524.47 1195.58 1506.59 1245.21 1481.25L1230.69 1452.75Z"
              fill="#006602"
              id="path63"
              data-segment="T17"
              @click="handleSegmentClick"
            />
            <path
              d="M1452.75 1230.69L1424.24 1216.16C1401.61 1260.56 1372.22 1300.95 1336.59 1336.59L1359.22 1359.22L1381.85 1381.85C1421.71 1341.99 1455.37 1295.92 1481.25 1245.22L1452.75 1230.7V1230.69Z"
              fill="#006602"
              id="path64"
              data-segment="T15"
              @click="handleSegmentClick"
            />
            <path
              d="M814.74 1438.62C804.24 1434.18 793.94 1429.38 783.84 1424.23L769.31 1452.74L754.79 1481.24C804.42 1506.58 858.48 1524.46 915.51 1533.42L920.51 1501.84L925.52 1470.23C887.49 1464.27 850.43 1453.71 814.74 1438.62Z"
              fill="#006602"
              id="path65"
              data-segment="T19"
              @click="handleSegmentClick"
            />
            <path
              d="M1079.48 1501.84L1074.47 1470.23C1050.01 1474.06 1025.15 1476 999.99 1476C974.83 1476 949.97 1474.06 925.51 1470.23L920.5 1501.84L915.5 1533.42C943.03 1537.75 971.24 1540 999.98 1540C1028.72 1540 1056.94 1537.75 1084.47 1533.42L1079.47 1501.84H1079.48Z"
              fill="#FF0000"
              id="path66"
              data-segment="T3"
              @click="handleSegmentClick"
            />
            <path
              d="M1359.21 1359.21L1336.58 1336.58C1300.95 1372.21 1260.55 1401.6 1216.15 1424.23L1230.68 1452.74L1245.2 1481.24C1295.9 1455.36 1341.97 1421.7 1381.83 1381.84L1359.2 1359.21H1359.21Z"
              fill="#FF0000"
              id="path67"
              data-segment="T2"
              @click="handleSegmentClick"
            />
            <path
              d="M1501.84 1079.48L1470.23 1074.47C1464.27 1112.5 1453.71 1149.56 1438.62 1185.25C1434.18 1195.75 1429.38 1206.05 1424.23 1216.15L1452.74 1230.68L1481.24 1245.2C1506.58 1195.57 1524.46 1141.51 1533.42 1084.48L1501.84 1079.48Z"
              fill="#FF0000"
              id="path68"
              data-segment="T10"
              @click="handleSegmentClick"
            />
            <path
              d="M1381.84 618.16L1359.21 640.79L1336.58 663.42C1372.21 699.05 1401.6 739.45 1424.23 783.85L1452.74 769.32L1481.24 754.8C1455.36 704.1 1421.7 658.03 1381.83 618.17L1381.84 618.16Z"
              fill="#006602"
              id="path69"
              data-segment="T4"
              @click="handleSegmentClick"
            />
            <path
              d="M1481.24 754.79L1452.74 769.31L1424.23 783.84C1429.38 793.94 1434.18 804.24 1438.62 814.74C1453.72 850.43 1464.27 887.49 1470.23 925.52L1501.84 920.51L1533.42 915.51C1524.46 858.48 1506.58 804.42 1481.24 754.79Z"
              fill="#FF0000"
              id="path70"
              data-segment="T13"
              @click="handleSegmentClick"
            />
            <path
              d="M1533.42 915.51L1501.84 920.51L1470.23 925.52C1474.06 949.98 1476 974.84 1476 1000C1476 1025.16 1474.06 1050.02 1470.23 1074.48L1501.84 1079.49L1533.42 1084.49C1537.75 1056.96 1540 1028.75 1540 1000C1540 971.25 1537.75 943.04 1533.42 915.51Z"
              fill="#006602"
              id="path71"
              data-segment="T6"
              @click="handleSegmentClick"
            />
            <path
              d="M1084.48 466.58L1079.48 498.16L1074.47 529.77C1112.5 535.73 1149.56 546.29 1185.25 561.38C1195.75 565.82 1206.05 570.62 1216.15 575.77L1230.68 547.26L1245.2 518.76C1195.57 493.42 1141.51 475.54 1084.48 466.58Z"
              fill="#006602"
              id="path72"
              data-segment="T1"
              @click="handleSegmentClick"
            />
            <path
              d="M1000 460C971.26 460 943.04 462.25 915.51 466.58L920.51 498.16L925.52 529.77C949.98 525.94 974.84 524 1000 524C1025.16 524 1050.02 525.94 1074.48 529.77L1079.49 498.16L1084.49 466.58C1056.96 462.25 1028.75 460 1000.01 460H1000Z"
              fill="#FF0000"
              id="path73"
              data-segment="T20"
              @click="handleSegmentClick"
            />
            <path
              d="M663.42 1336.58C627.79 1300.95 598.4 1260.55 575.77 1216.16L547.26 1230.69L518.76 1245.21C544.64 1295.91 578.3 1341.98 618.17 1381.84L640.8 1359.21L663.43 1336.58H663.42Z"
              fill="#006602"
              id="path74"
              data-segment="T16"
              @click="handleSegmentClick"
            />
            <path
              d="M920.52 498.16L915.52 466.58C858.49 475.54 804.43 493.42 754.8 518.76L769.32 547.26L783.85 575.77C793.95 570.62 804.25 565.83 814.75 561.38C850.44 546.28 887.5 535.73 925.53 529.77L920.52 498.16Z"
              fill="#006602"
              id="path75"
              data-segment="T5"
              @click="handleSegmentClick"
            />
            <path
              d="M1245.2 518.76L1230.68 547.26L1216.15 575.77C1260.55 598.4 1300.94 627.79 1336.58 663.42L1359.21 640.79L1381.84 618.16C1341.98 578.3 1295.91 544.64 1245.21 518.76H1245.2Z"
              fill="#FF0000"
              id="path76"
              data-segment="T18"
              @click="handleSegmentClick"
            />
            <path
              d="M769.31 547.25L754.79 518.75C704.09 544.63 658.02 578.29 618.16 618.15L640.79 640.78L663.42 663.41C699.05 627.78 739.45 598.39 783.85 575.76L769.32 547.25H769.31Z"
              fill="#FF0000"
              id="path77"
              data-segment="T12"
              @click="handleSegmentClick"
            />
            <path
              d="M524 1000C524 974.84 525.94 949.98 529.77 925.52L498.16 920.51L466.58 915.51C462.25 943.04 460 971.25 460 1000C460 1028.75 462.25 1056.96 466.58 1084.49L498.16 1079.49L529.77 1074.48C525.94 1050.02 524 1025.16 524 1000Z"
              fill="#006602"
              id="path78"
              data-segment="T11"
              @click="handleSegmentClick"
            />
            <path
              d="M561.38 1185.26C546.28 1149.57 535.73 1112.51 529.77 1074.48L498.16 1079.49L466.58 1084.49C475.54 1141.52 493.42 1195.58 518.76 1245.21L547.26 1230.69L575.77 1216.16C570.62 1206.06 565.82 1195.76 561.38 1185.26Z"
              fill="#FF0000"
              id="path79"
              data-segment="T8"
              @click="handleSegmentClick"
            />
            <path
              d="M640.79 640.79L618.16 618.16C578.3 658.02 544.64 704.09 518.76 754.79L547.26 769.31L575.77 783.84C598.4 739.45 627.79 699.05 663.42 663.41L640.79 640.78V640.79Z"
              fill="#006602"
              id="path80"
              data-segment="T9"
              @click="handleSegmentClick"
            />
            <path
              d="M547.25 769.31L518.75 754.79C493.41 804.42 475.53 858.48 466.57 915.51L498.15 920.51L529.76 925.52C535.72 887.49 546.28 850.43 561.37 814.74C565.81 804.24 570.61 793.94 575.76 783.84L547.25 769.31Z"
              fill="#FF0000"
              id="path81"
              data-segment="T14"
              @click="handleSegmentClick"
            />
            <path
              d="M964.64 1035.36L925.75 1074.25C933.5 1082 942.46 1088.54 952.32 1093.58L977.3 1044.55C972.61 1042.15 968.33 1039.05 964.64 1035.36Z"
              fill="#FFFCE3"
              id="path82"
            />
            <path
              d="M955.45 1022.7L906.42 1047.68C911.45 1057.54 918 1066.5 925.75 1074.25L964.64 1035.36C960.95 1031.67 957.85 1027.4 955.45 1022.7Z"
              fill="#FFFCE3"
              id="path83"
            />
            <path
              d="M950.61 1007.82L896.28 1016.43C898.02 1027.52 901.5 1038.03 906.42 1047.68L955.45 1022.7C953.1 1018.1 951.44 1013.1 950.61 1007.82Z"
              fill="#FFFCE3"
              id="path84"
            />
            <path
              d="M977.3 1044.55L952.32 1093.58C961.97 1098.51 972.48 1101.98 983.57 1103.72L992.18 1049.39C986.9 1048.56 981.9 1046.9 977.3 1044.55Z"
              fill="#FFFCE3"
              id="path85"
            />
            <path
              d="M955.45 977.3L906.42 952.32C901.49 961.97 898.02 972.48 896.28 983.57L950.61 992.18C951.44 986.9 953.1 981.9 955.45 977.3Z"
              fill="#FFFCE3"
              id="path86"
            />
            <path
              d="M992.18 950.61L983.57 896.28C972.48 898.02 961.97 901.5 952.32 906.42L977.3 955.45C981.9 953.1 986.9 951.44 992.18 950.61Z"
              fill="#FFFCE3"
              id="path87"
            />
            <path
              d="M977.3 955.45L952.32 906.42C942.46 911.45 933.5 918 925.75 925.75L964.64 964.64C968.33 960.95 972.6 957.85 977.3 955.45Z"
              fill="#FFFCE3"
              id="path88"
            />
            <path
              d="M950 1000C950 997.34 950.21 994.73 950.61 992.18L896.28 983.57C895.44 988.92 895 994.41 895 1000C895 1005.59 895.44 1011.07 896.28 1016.43L950.61 1007.82C950.21 1005.27 950 1002.66 950 1000Z"
              fill="#FFFCE3"
              id="path89"
            />
            <path
              d="M1000 1050C997.34 1050 994.73 1049.79 992.18 1049.39L983.57 1103.72C988.92 1104.56 994.41 1105 1000 1105C1005.59 1105 1011.07 1104.56 1016.43 1103.72L1007.82 1049.39C1005.27 1049.79 1002.66 1050 1000 1050Z"
              fill="#FFFCE3"
              id="path90"
            />
            <path
              d="M964.64 964.64L925.75 925.75C918 933.5 911.46 942.46 906.42 952.32L955.45 977.3C957.85 972.61 960.95 968.33 964.64 964.64Z"
              fill="#FFFCE3"
              id="path91"
            />
            <path
              d="M983.57 896.28L992.18 950.61C994.73 950.21 997.34 950 1000 950C1002.66 950 1005.27 950.21 1007.82 950.61L1016.43 896.28C1011.08 895.44 1005.59 895 1000 895C994.41 895 988.93 895.44 983.57 896.28Z"
              fill="#FFFCE3"
              id="path92"
            />
            <path
              d="M1044.55 977.3L1093.58 952.32C1088.55 942.46 1082 933.5 1074.25 925.75L1035.36 964.64C1039.05 968.33 1042.15 972.6 1044.55 977.3Z"
              fill="#FFFCE3"
              id="path93"
            />
            <path
              d="M1007.82 1049.39L1016.43 1103.72C1027.52 1101.98 1038.03 1098.5 1047.68 1093.58L1022.7 1044.55C1018.1 1046.9 1013.1 1048.56 1007.82 1049.39Z"
              fill="#FFFCE3"
              id="path94"
            />
            <path
              d="M1022.7 955.45L1047.68 906.42C1038.03 901.49 1027.52 898.02 1016.43 896.28L1007.82 950.61C1013.1 951.44 1018.1 953.1 1022.7 955.45Z"
              fill="#FFFCE3"
              id="path95"
            />
            <path
              d="M1035.36 964.64L1074.25 925.75C1066.5 918 1057.54 911.46 1047.68 906.42L1022.7 955.45C1027.39 957.85 1031.67 960.95 1035.36 964.64Z"
              fill="#FFFCE3"
              id="path96"
            />
            <path
              d="M1049.39 992.18L1103.72 983.57C1101.98 972.48 1098.5 961.97 1093.58 952.32L1044.55 977.3C1046.9 981.9 1048.56 986.9 1049.39 992.18Z"
              fill="#FFFCE3"
              id="path97"
            />
            <path
              d="M1022.7 1044.55L1047.68 1093.58C1057.54 1088.55 1066.5 1082 1074.25 1074.25L1035.36 1035.36C1031.67 1039.05 1027.4 1042.15 1022.7 1044.55Z"
              fill="#FFFCE3"
              id="path98"
            />
            <path
              d="M1035.36 1035.36L1074.25 1074.25C1082 1066.5 1088.55 1057.54 1093.58 1047.68L1044.55 1022.7C1042.15 1027.39 1039.05 1031.67 1035.36 1035.36Z"
              fill="#FFFCE3"
              id="path99"
            />
            <path
              d="M1044.55 1022.7L1093.58 1047.68C1098.51 1038.03 1101.98 1027.52 1103.72 1016.43L1049.39 1007.82C1048.56 1013.1 1046.9 1018.1 1044.55 1022.7Z"
              fill="#FFFCE3"
              id="path100"
            />
            <path
              d="M1050 1000C1050 1002.66 1049.79 1005.27 1049.39 1007.82L1103.72 1016.43C1104.56 1011.08 1105 1005.59 1105 1000C1105 994.41 1104.56 988.93 1103.72 983.57L1049.39 992.18C1049.79 994.73 1050 997.34 1050 1000Z"
              fill="#FFFCE3"
              id="path101"
            />
            <path
              d="M1103.72 983.57C1101.98 972.48 1098.5 961.97 1093.58 952.32C1088.55 942.46 1082 933.5 1074.25 925.75C1066.5 918 1057.54 911.46 1047.68 906.42C1038.03 901.49 1027.52 898.02 1016.43 896.28C1011.08 895.44 1005.59 895 1000 895C994.41 895 988.93 895.44 983.57 896.28C972.48 898.02 961.97 901.5 952.32 906.42C942.46 911.45 933.5 918 925.75 925.75C918 933.5 911.46 942.46 906.42 952.32C901.49 961.97 898.02 972.48 896.28 983.57C895.44 988.92 895 994.41 895 1000C895 1005.59 895.44 1011.07 896.28 1016.43C898.02 1027.52 901.5 1038.03 906.42 1047.68C911.45 1057.54 918 1066.5 925.75 1074.25C933.5 1082 942.46 1088.54 952.32 1093.58C961.97 1098.51 972.48 1101.98 983.57 1103.72C988.92 1104.56 994.41 1105 1000 1105C1005.59 1105 1011.07 1104.56 1016.43 1103.72C1027.52 1101.98 1038.03 1098.5 1047.68 1093.58C1057.54 1088.55 1066.5 1082 1074.25 1074.25C1082 1066.5 1088.55 1057.54 1093.58 1047.68C1098.51 1038.03 1101.98 1027.52 1103.72 1016.43C1104.56 1011.08 1105 1005.59 1105 1000C1105 994.41 1104.56 988.93 1103.72 983.57ZM1044.55 1022.7C1042.15 1027.39 1039.05 1031.67 1035.36 1035.36C1031.67 1039.05 1027.4 1042.15 1022.7 1044.55C1018.1 1046.9 1013.1 1048.56 1007.82 1049.39C1005.27 1049.79 1002.66 1050 1000 1050C997.34 1050 994.73 1049.79 992.18 1049.39C986.9 1048.56 981.9 1046.9 977.3 1044.55C972.61 1042.15 968.33 1039.05 964.64 1035.36C960.95 1031.67 957.85 1027.4 955.45 1022.7C953.1 1018.1 951.44 1013.1 950.61 1007.82C950.21 1005.27 950 1002.66 950 1000C950 997.34 950.21 994.73 950.61 992.18C951.44 986.9 953.1 981.9 955.45 977.3C957.85 972.61 960.95 968.33 964.64 964.64C968.33 960.95 972.6 957.85 977.3 955.45C981.9 953.1 986.9 951.44 992.18 950.61C994.73 950.21 997.34 950 1000 950C1002.66 950 1005.27 950.21 1007.82 950.61C1013.1 951.44 1018.1 953.1 1022.7 955.45C1027.39 957.85 1031.67 960.95 1035.36 964.64C1039.05 968.33 1042.15 972.6 1044.55 977.3C1046.9 981.9 1048.56 986.9 1049.39 992.18C1049.79 994.73 1050 997.34 1050 1000C1050 1002.66 1049.79 1005.27 1049.39 1007.82C1048.56 1013.1 1046.9 1018.1 1044.55 1022.7Z"
              fill="#006602"
              id="path102"
              data-segment="25"
              @click="handleSegmentClick"
            />
            <path
              d="M1044.55 1022.7C1046.9 1018.1 1048.56 1013.1 1049.39 1007.82L1000 1000L1044.55 1022.7Z"
              fill="#FFFCE3"
              id="path103"
            />
            <path
              d="M1044.55 1022.7C1046.9 1018.1 1048.56 1013.1 1049.39 1007.82L1000 1000L1044.55 1022.7Z"
              fill="#FFFCE3"
              id="path104"
            />
            <path
              d="M1049.39 992.18C1048.56 986.9 1046.9 981.9 1044.55 977.3L1000 1000L1049.39 992.18Z"
              fill="#FFFCE3"
              id="path105"
            />
            <path
              d="M1049.39 992.18C1048.56 986.9 1046.9 981.9 1044.55 977.3L1000 1000L1049.39 992.18Z"
              fill="#FFFCE3"
              id="path106"
            />
            <path
              d="M1000 1000L1049.39 1007.82C1049.79 1005.27 1050 1002.66 1050 1000C1050 997.34 1049.79 994.73 1049.39 992.18L1000 1000Z"
              fill="#FFFCE3"
              id="path107"
            />
            <path
              d="M1000 1000L1049.39 1007.82C1049.79 1005.27 1050 1002.66 1050 1000C1050 997.34 1049.79 994.73 1049.39 992.18L1000 1000Z"
              fill="#FFFCE3"
              id="path108"
            />
            <path
              d="M1000 1000L1035.36 1035.36C1039.05 1031.67 1042.15 1027.4 1044.55 1022.7L1000 1000Z"
              fill="#FFFCE3"
              id="path109"
            />
            <path
              d="M1000 1000L1035.36 1035.36C1039.05 1031.67 1042.15 1027.4 1044.55 1022.7L1000 1000Z"
              fill="#FFFCE3"
              id="path110"
            />
            <path
              d="M1000 1000L1022.7 1044.55C1027.39 1042.15 1031.67 1039.05 1035.36 1035.36L1000 1000Z"
              fill="#FFFCE3"
              id="path111"
            />
            <path
              d="M1000 1000L1022.7 1044.55C1027.39 1042.15 1031.67 1039.05 1035.36 1035.36L1000 1000Z"
              fill="#FFFCE3"
              id="path112"
            />
            <path
              d="M1000 1000L1022.7 955.45C1018.1 953.1 1013.1 951.44 1007.82 950.61L1000 1000Z"
              fill="#FFFCE3"
              id="path113"
            />
            <path
              d="M1000 1000L1022.7 955.45C1018.1 953.1 1013.1 951.44 1007.82 950.61L1000 1000Z"
              fill="#FFFCE3"
              id="path114"
            />
            <path
              d="M1000 1000L992.18 950.61C986.9 951.44 981.9 953.1 977.3 955.45L1000 1000Z"
              fill="#FFFCE3"
              id="path115"
            />
            <path
              d="M1000 1000L992.18 950.61C986.9 951.44 981.9 953.1 977.3 955.45L1000 1000Z"
              fill="#FFFCE3"
              id="path116"
            />
            <path
              d="M1000 1000L1007.82 950.61C1005.27 950.21 1002.66 950 1000 950C997.34 950 994.73 950.21 992.18 950.61L1000 1000Z"
              fill="#FFFCE3"
              id="path117"
            />
            <path
              d="M1000 1000L1007.82 950.61C1005.27 950.21 1002.66 950 1000 950C997.34 950 994.73 950.21 992.18 950.61L1000 1000Z"
              fill="#FFFCE3"
              id="path118"
            />
            <path
              d="M1000 1000L1035.36 964.64C1031.67 960.95 1027.4 957.85 1022.7 955.45L1000 1000Z"
              fill="#FFFCE3"
              id="path119"
            />
            <path
              d="M1000 1000L1035.36 964.64C1031.67 960.95 1027.4 957.85 1022.7 955.45L1000 1000Z"
              fill="#FFFCE3"
              id="path120"
            />
            <path
              d="M1007.82 1049.39C1013.1 1048.56 1018.1 1046.9 1022.7 1044.55L1000 1000L1007.82 1049.39Z"
              fill="#FFFCE3"
              id="path121"
            />
            <path
              d="M1007.82 1049.39C1013.1 1048.56 1018.1 1046.9 1022.7 1044.55L1000 1000L1007.82 1049.39Z"
              fill="#FFFCE3"
              id="path122"
            />
            <path
              d="M1000 1000L1044.55 977.3C1042.15 972.61 1039.05 968.33 1035.36 964.64L1000 1000Z"
              fill="#FFFCE3"
              id="path123"
            />
            <path
              d="M1000 1000L1044.55 977.3C1042.15 972.61 1039.05 968.33 1035.36 964.64L1000 1000Z"
              fill="#FFFCE3"
              id="path124"
            />
            <path
              d="M1000 1000L992.18 1049.39C994.73 1049.79 997.34 1050 1000 1050C1002.66 1050 1005.27 1049.79 1007.82 1049.39L1000 1000Z"
              fill="#FFFCE3"
              id="path125"
            />
            <path
              d="M1000 1000L992.18 1049.39C994.73 1049.79 997.34 1050 1000 1050C1002.66 1050 1005.27 1049.79 1007.82 1049.39L1000 1000Z"
              fill="#FFFCE3"
              id="path126"
            />
            <path
              d="M964.64 964.64C960.95 968.33 957.85 972.6 955.45 977.3L1000 1000L964.64 964.64Z"
              fill="#FFFCE3"
              id="path127"
            />
            <path
              d="M964.64 964.64C960.95 968.33 957.85 972.6 955.45 977.3L1000 1000L964.64 964.64Z"
              fill="#FFFCE3"
              id="path128"
            />
            <path
              d="M1000 1000L955.45 977.3C953.1 981.9 951.44 986.9 950.61 992.18L1000 1000Z"
              fill="#FFFCE3"
              id="path129"
            />
            <path
              d="M1000 1000L955.45 977.3C953.1 981.9 951.44 986.9 950.61 992.18L1000 1000Z"
              fill="#FFFCE3"
              id="path130"
            />
            <path
              d="M1000 1000L977.3 955.45C972.61 957.85 968.33 960.95 964.64 964.64L1000 1000Z"
              fill="#FFFCE3"
              id="path131"
            />
            <path
              d="M1000 1000L977.3 955.45C972.61 957.85 968.33 960.95 964.64 964.64L1000 1000Z"
              fill="#FFFCE3"
              id="path132"
            />
            <path
              d="M1000 1000L964.64 1035.36C968.33 1039.05 972.6 1042.15 977.3 1044.55L1000 1000Z"
              fill="#FFFCE3"
              id="path133"
            />
            <path
              d="M1000 1000L964.64 1035.36C968.33 1039.05 972.6 1042.15 977.3 1044.55L1000 1000Z"
              fill="#FFFCE3"
              id="path134"
            />
            <path
              d="M950.61 992.18C950.21 994.73 950 997.34 950 1000C950 1002.66 950.21 1005.27 950.61 1007.82L1000 1000L950.61 992.18Z"
              fill="#FFFCE3"
              id="path135"
            />
            <path
              d="M950.61 992.18C950.21 994.73 950 997.34 950 1000C950 1002.66 950.21 1005.27 950.61 1007.82L1000 1000L950.61 992.18Z"
              fill="#FFFCE3"
              id="path136"
            />
            <path
              d="M1000 1000L977.3 1044.55C981.9 1046.9 986.9 1048.56 992.18 1049.39L1000 1000Z"
              fill="#FFFCE3"
              id="path137"
            />
            <path
              d="M1000 1000L977.3 1044.55C981.9 1046.9 986.9 1048.56 992.18 1049.39L1000 1000Z"
              fill="#FFFCE3"
              id="path138"
            />
            <path
              d="M1000 1000L950.61 1007.82C951.44 1013.1 953.1 1018.1 955.45 1022.7L1000 1000Z"
              fill="#FFFCE3"
              id="path139"
            />
            <path
              d="M1000 1000L950.61 1007.82C951.44 1013.1 953.1 1018.1 955.45 1022.7L1000 1000Z"
              fill="#FFFCE3"
              id="path140"
            />
            <path
              d="M955.45 1022.7C957.85 1027.39 960.95 1031.67 964.64 1035.36L1000 1000L955.45 1022.7Z"
              fill="#FFFCE3"
              id="path141"
            />
            <path
              d="M955.45 1022.7C957.85 1027.39 960.95 1031.67 964.64 1035.36L1000 1000L955.45 1022.7Z"
              fill="#FFFCE3"
              id="path142"
            />
            <path
              d="M1049.39 992.18C1048.56 986.9 1046.9 981.9 1044.55 977.3C1042.15 972.61 1039.05 968.33 1035.36 964.64C1031.67 960.95 1027.4 957.85 1022.7 955.45C1018.1 953.1 1013.1 951.44 1007.82 950.61C1005.27 950.21 1002.66 950 1000 950C997.34 950 994.73 950.21 992.18 950.61C986.9 951.44 981.9 953.1 977.3 955.45C972.61 957.85 968.33 960.95 964.64 964.64C960.95 968.33 957.85 972.6 955.45 977.3C953.1 981.9 951.44 986.9 950.61 992.18C950.21 994.73 950 997.34 950 1000C950 1002.66 950.21 1005.27 950.61 1007.82C951.44 1013.1 953.1 1018.1 955.45 1022.7C957.85 1027.39 960.95 1031.67 964.64 1035.36C968.33 1039.05 972.6 1042.15 977.3 1044.55C981.9 1046.9 986.9 1048.56 992.18 1049.39C994.73 1049.79 997.34 1050 1000 1050C1002.66 1050 1005.27 1049.79 1007.82 1049.39C1013.1 1048.56 1018.1 1046.9 1022.7 1044.55C1027.39 1042.15 1031.67 1039.05 1035.36 1035.36C1039.05 1031.67 1042.15 1027.4 1044.55 1022.7C1046.9 1018.1 1048.56 1013.1 1049.39 1007.82C1049.79 1005.27 1050 1002.66 1050 1000C1050 997.34 1049.79 994.73 1049.39 992.18Z"
              fill="#FF0000"
              id="path143"
              data-segment="50"
              @click="handleSegmentClick"
            />
            <g id="g144">
              <path
                d="M1000 160C536.08 160 160 536.08 160 1000C160 1463.92 536.08 1840 1000 1840C1463.92 1840 1840 1463.92 1840 1000C1840 536.08 1463.92 160 1000 160ZM1643.51 1433.82C1615.74 1474.92 1583.85 1513.58 1548.72 1548.71C1513.59 1583.84 1474.93 1615.74 1433.83 1643.5C1392.35 1671.52 1348.01 1695.59 1302.04 1715.04C1206.41 1755.49 1104.79 1776 1000.01 1776C895.23 1776 793.6 1755.49 697.98 1715.04C652.01 1695.6 607.67 1671.53 566.19 1643.5C525.09 1615.73 486.43 1583.84 451.3 1548.71C416.17 1513.58 384.27 1474.92 356.51 1433.82C328.49 1392.34 304.42 1348 284.98 1302.03C244.53 1206.4 224.02 1104.78 224.02 1000C224.02 895.22 244.53 793.59 284.98 697.97C304.42 652 328.49 607.66 356.51 566.18C384.28 525.08 416.17 486.42 451.3 451.29C486.43 416.16 525.09 384.27 566.19 356.5C607.67 328.48 652.01 304.41 697.98 284.96C793.61 244.51 895.23 224 1000.01 224C1104.79 224 1206.42 244.51 1302.04 284.96C1348.01 304.4 1392.35 328.47 1433.83 356.5C1474.93 384.27 1513.59 416.16 1548.72 451.29C1583.85 486.42 1615.75 525.08 1643.51 566.18C1671.53 607.66 1695.6 652 1715.05 697.97C1755.5 793.6 1776.01 895.22 1776.01 1000C1776.01 1104.78 1755.5 1206.41 1715.05 1302.03C1695.61 1348 1671.54 1392.34 1643.51 1433.82Z"
                stroke="#9D9D9C"
                stroke-width="5"
                stroke-miterlimit="10"
                id="path144"
              />
            </g>
            <g id="g145">
              <path
                d="M1000 460C701.77 460 460 701.77 460 1000C460 1298.23 701.77 1540 1000 1540C1298.23 1540 1540 1298.23 1540 1000C1540 701.77 1298.23 460 1000 460ZM1000 1476C935.71 1476 873.38 1463.42 814.74 1438.62C758.07 1414.65 707.15 1380.32 663.42 1336.58C619.68 1292.84 585.35 1241.93 561.38 1185.26C536.58 1126.62 524 1064.29 524 1000C524 935.71 536.58 873.38 561.38 814.74C585.35 758.07 619.68 707.15 663.42 663.42C707.16 619.68 758.07 585.35 814.74 561.38C873.38 536.58 935.71 524 1000 524C1064.29 524 1126.62 536.58 1185.26 561.38C1241.93 585.35 1292.85 619.68 1336.58 663.42C1380.32 707.16 1414.65 758.07 1438.62 814.74C1463.42 873.38 1476 935.71 1476 1000C1476 1064.29 1463.42 1126.62 1438.62 1185.26C1414.65 1241.93 1380.32 1292.85 1336.58 1336.58C1292.84 1380.32 1241.93 1414.65 1185.26 1438.62C1126.62 1463.42 1064.29 1476 1000 1476Z"
                stroke="#9D9D9C"
                stroke-width="5"
                stroke-miterlimit="10"
                id="path145"
              />
            </g>
            <g id="g146">
              <path
                d="M1000 895C942.01 895 895 942.01 895 1000C895 1057.99 942.01 1105 1000 1105C1057.99 1105 1105 1057.99 1105 1000C1105 942.01 1057.99 895 1000 895ZM1000 1050C972.39 1050 950 1027.61 950 1000C950 972.39 972.39 950 1000 950C1027.61 950 1050 972.39 1050 1000C1050 1027.61 1027.61 1050 1000 1050Z"
                stroke="#9D9D9C"
                stroke-width="5"
                stroke-miterlimit="10"
                id="path146"
              />
            </g>
            <g id="g147">
              <path
                d="M1569.68 1781.9L1568.94 1790.45L1509.12 1785.29L1509.4 1782.07C1555.3 1742.24 1560.71 1734.87 1561.73 1723.04C1562.59 1713.01 1556.19 1705.64 1545.63 1704.73C1534.75 1703.79 1526.81 1710.11 1525.95 1720.05L1525.75 1722.34L1516.82 1721.57L1517.02 1719.28C1518.34 1703.94 1530.39 1694.8 1546.37 1696.18C1562.1 1697.54 1572.04 1708.86 1570.75 1723.82C1569.64 1736.67 1564.09 1745.47 1527.29 1778.25L1569.67 1781.91L1569.68 1781.9Z"
                fill="#9D9D9C"
                id="path147"
              />
            </g>
            <g id="g148">
              <path
                d="M715.53 114.61C733.38 114.9 746.07 127.81 745.81 144.03C745.53 160.96 732.35 173.01 715.71 172.74C699.11 172.47 687.11 160.71 686.62 144.83L695.49 144.97C696.06 156.13 704.35 163.97 715.85 164.15C727.61 164.34 736.57 155.73 736.76 143.87C736.95 132.04 727.66 122.9 715.12 122.7L692.68 122.34L691.32 120.74L696.39 80.3899L740.69 81.1099L740.56 89.3999L703.72 88.7999L700.51 114.36L715.52 114.6L715.53 114.61Z"
                fill="#9D9D9C"
                id="path148"
              />
            </g>
            <g id="g150">
              <path
                d="M432.87 212.07L437.01 302.42L428.15 302.83L424.39 220.77L406.18 221.6L405.8 213.31L432.86 212.07H432.87Z"
                fill="#9D9D9C"
                id="path149"
              />
              <path
                d="M514.19 294.6L513.95 303.18L453.93 301.47L454.02 298.23C497.56 255.82 502.54 248.16 502.88 236.29C503.17 226.23 496.35 219.23 485.76 218.93C474.84 218.62 467.28 225.38 466.99 235.36L466.92 237.66L457.96 237.41L458.03 235.11C458.47 219.72 469.97 209.9 486 210.36C501.78 210.81 512.36 221.55 511.93 236.56C511.56 249.45 506.52 258.56 471.67 293.4L514.2 294.61L514.19 294.6Z"
                fill="#9D9D9C"
                id="path150"
              />
            </g>
            <g id="g151">
              <path
                d="M288.43 444.52C288.29 453.06 284.79 463.72 278.16 474.85L259.48 506.23L249.62 506.07C263.77 482.03 267.81 475.22 270.81 469.31C266.2 471.98 260.77 473.41 255.09 473.32C237.86 473.04 224.81 460.06 225.08 443.5C225.36 425.9 238.88 413.49 257.2 413.79C275.67 414.09 288.72 426.98 288.43 444.53V444.52ZM279.37 444.38C279.57 431.96 270.19 422.58 257.15 422.37C244.11 422.16 234.33 431.14 234.13 443.65C233.93 456.16 243.41 465.32 256.45 465.53C269.49 465.74 279.17 456.8 279.37 444.38Z"
                fill="#9D9D9C"
                id="path151"
              />
            </g>
            <g id="g153">
              <path
                d="M97.79 671.17L102.05 761.51L93.19 761.93L89.32 679.88L71.12 680.74L70.73 672.45L97.79 671.17Z"
                fill="#9D9D9C"
                id="path152"
              />
              <path
                d="M183.28 742.76L173.18 742.47L172.62 762.02L163.85 761.77L164.41 742.22L117.32 740.88L117.39 738.49L151.75 670.75L161.13 671.02L129.64 733.23L164.73 734.23L167.02 704.55L174.27 704.76L173.42 734.48L183.52 734.77L183.29 742.78L183.28 742.76Z"
                fill="#9D9D9C"
                id="path153"
              />
            </g>
            <g id="g155">
              <path
                d="M72.58 954.71L75.17 1045.11L66.31 1045.36L63.96 963.25L45.74 963.77L45.5 955.48L72.58 954.71Z"
                fill="#9D9D9C"
                id="path154"
              />
              <path
                d="M120.5 955.18L117.92 1045.59L109.06 1045.34L111.4 963.23L93.1802 962.71L93.4202 954.42L120.5 955.19V955.18Z"
                fill="#9D9D9C"
                id="path155"
              />
            </g>
            <g id="g156">
              <path
                d="M144.63 1280.5C153.34 1284.59 158.8 1293.39 158.64 1303.71C158.38 1319.64 145.26 1330.68 126.64 1330.38C107.83 1330.07 95.3499 1318.91 95.6099 1302.69C95.7699 1292.55 101.54 1283.98 110.48 1279.96C103.82 1275.98 99.6499 1268.38 99.7899 1259.95C100.01 1246.04 111.53 1236.6 128.15 1236.87C144.77 1237.14 155.88 1246.85 155.65 1260.85C155.51 1269.42 151.14 1276.97 144.62 1280.51L144.63 1280.5ZM108.66 1260.08C108.5 1269.86 116.32 1276.39 127.51 1276.57C138.61 1276.75 146.63 1270.48 146.79 1260.7C146.94 1251.37 139.36 1244.77 128.03 1244.58C116.6 1244.4 108.81 1250.75 108.66 1260.08ZM149.58 1303.84C149.77 1292.19 140.59 1284.51 127.39 1284.3C114.28 1284.09 104.86 1291.46 104.67 1303.11C104.49 1314.27 113.43 1322.14 126.77 1322.36C140.2 1322.58 149.4 1315 149.58 1303.84Z"
                fill="#9D9D9C"
                id="path156"
              />
            </g>
            <g id="g158">
              <path
                d="M227.33 1494.76L231.71 1585.09L222.85 1585.52L218.87 1503.48L200.67 1504.36L200.27 1496.07L227.33 1494.76Z"
                fill="#9D9D9C"
                id="path157"
              />
              <path
                d="M314.65 1557.2C314.15 1574.79 300.48 1587.04 282.07 1586.52C263.51 1585.99 250.52 1572.94 251.03 1555.39C251.27 1546.85 255.19 1536.34 261.86 1525.19L280.94 1493.47L290.8 1493.75C276.11 1518.34 272.05 1525.14 269.06 1530.92C273.85 1528.22 279.39 1526.84 284.92 1527C301.29 1527.47 315.16 1539.69 314.66 1557.2H314.65ZM305.6 1556.94C305.96 1544.34 296.59 1535.06 283.46 1534.69C270.33 1534.32 260.44 1543.14 260.09 1555.64C259.74 1568.05 269.1 1577.56 282.23 1577.93C295.36 1578.3 305.25 1569.44 305.61 1556.94H305.6Z"
                fill="#9D9D9C"
                id="path158"
              />
            </g>
            <g id="g159">
              <path
                d="M490.15 1698.79L490.12 1700.58L452.78 1788.67L443.13 1788.51L477.96 1706.88L429.85 1706.1L429.98 1697.81L490.15 1698.79Z"
                fill="#9D9D9C"
                id="path159"
              />
            </g>
            <g id="g161">
              <path
                d="M687.79 1827.51L692.16 1917.85L683.3 1918.28L679.33 1836.23L661.13 1837.11L660.73 1828.82L687.79 1827.51Z"
                fill="#9D9D9C"
                id="path160"
              />
              <path
                d="M773.39 1856.83C773.15 1865.37 769.51 1875.99 762.75 1887.03L743.68 1918.18L733.82 1917.9C748.27 1894.03 752.39 1887.27 755.46 1881.4C750.81 1884.01 745.37 1885.38 739.69 1885.22C722.46 1884.73 709.57 1871.58 710.05 1855.03C710.55 1837.44 724.22 1825.19 742.53 1825.71C760.99 1826.24 773.89 1839.29 773.39 1856.84V1856.83ZM764.33 1856.57C764.68 1844.16 755.42 1834.65 742.38 1834.28C729.34 1833.91 719.46 1842.77 719.1 1855.28C718.74 1867.79 728.11 1877.06 741.15 1877.43C754.19 1877.8 763.98 1868.98 764.33 1856.57Z"
                fill="#9D9D9C"
                id="path161"
              />
            </g>
            <g id="g162">
              <path
                d="M1029.46 1934.51C1029.18 1951.53 1016 1963.49 999.17 1963.22C982.76 1962.95 970.95 1951.39 970.27 1935.32L979.14 1935.46C979.8 1946.72 987.9 1954.45 999.31 1954.64C1011.26 1954.83 1020.22 1946.12 1020.41 1934.08C1020.6 1922.15 1011.31 1913.2 998.58 1913L989.29 1912.85L989.32 1910.82L1012.82 1879.7L974.32 1879.07L974.45 1870.78L1027.02 1871.63L1026.98 1873.94L1003.05 1905.72C1018.89 1907.24 1029.7 1919.27 1029.45 1934.51H1029.46Z"
                fill="#9D9D9C"
                id="path162"
              />
            </g>
            <g id="g164">
              <path
                d="M1255.88 1825.21L1253.48 1915.62L1244.62 1915.38L1246.8 1833.26L1228.58 1832.78L1228.8 1824.49L1255.88 1825.21Z"
                fill="#9D9D9C"
                id="path163"
              />
              <path
                d="M1337.66 1831.67L1337.48 1833.46L1293.04 1918.2L1283.43 1917.25L1324.84 1838.75L1276.95 1834.03L1277.76 1825.77L1337.65 1831.68L1337.66 1831.67Z"
                fill="#9D9D9C"
                id="path164"
              />
            </g>
            <g id="g166">
              <path
                d="M1713.69 1497.03L1724.14 1586.87L1715.33 1587.9L1705.84 1506.3L1687.74 1508.41L1686.78 1500.17L1713.69 1497.04V1497.03Z"
                fill="#9D9D9C"
                id="path165"
              />
              <path
                d="M1767.77 1527C1785.61 1526.26 1799.02 1538.42 1799.69 1554.63C1800.39 1571.55 1787.92 1584.34 1771.29 1585.03C1754.7 1585.72 1742.04 1574.67 1740.64 1558.84L1749.5 1558.47C1750.71 1569.58 1759.44 1576.93 1770.93 1576.45C1782.68 1575.97 1791.13 1566.85 1790.64 1555C1790.15 1543.18 1780.35 1534.58 1767.82 1535.1L1745.4 1536.03L1743.95 1534.51L1746.7 1493.93L1790.97 1492.1L1791.31 1500.39L1754.5 1501.91L1752.77 1527.61L1767.78 1526.99L1767.77 1527Z"
                fill="#9D9D9C"
                id="path166"
              />
            </g>
            <g id="g168">
              <path
                d="M1839.18 1238.86L1844.12 1329.16L1835.27 1329.64L1830.78 1247.62L1812.58 1248.62L1812.13 1240.34L1839.18 1238.86Z"
                fill="#9D9D9C"
                id="path167"
              />
              <path
                d="M1864.71 1282.58C1865.53 1253.75 1879.27 1236.25 1900.53 1236.86C1921.69 1237.46 1934.41 1255.71 1933.59 1284.54C1932.77 1313.3 1918.93 1330.96 1897.86 1330.36C1876.7 1329.76 1863.89 1311.34 1864.71 1282.58ZM1924.53 1284.29C1925.21 1260.51 1915.94 1245.89 1900.28 1245.44C1884.53 1244.99 1874.44 1259.06 1873.76 1282.84C1873.08 1306.66 1882.27 1321.33 1898.1 1321.78C1913.74 1322.23 1923.85 1308.11 1924.53 1284.28V1284.29Z"
                fill="#9D9D9C"
                id="path168"
              />
            </g>
            <g id="g169">
              <path
                d="M1948.82 1016.79C1948.54 1034.39 1935.02 1046.8 1916.61 1046.5C1898.05 1046.2 1884.9 1033.31 1885.19 1015.75C1885.33 1007.21 1889.12 996.65 1895.65 985.43L1914.34 953.48L1924.2 953.64C1909.82 978.41 1905.83 985.25 1902.92 991.08C1907.68 988.32 1913.2 986.87 1918.73 986.96C1935.1 987.23 1949.12 999.27 1948.84 1016.79H1948.82ZM1939.76 1016.64C1939.97 1004.03 1930.48 994.88 1917.35 994.66C1904.21 994.45 1894.44 1003.39 1894.24 1015.9C1894.04 1028.31 1903.52 1037.7 1916.65 1037.92C1929.79 1038.13 1939.56 1029.15 1939.76 1016.64Z"
                fill="#9D9D9C"
                id="path169"
              />
            </g>
            <g id="g171">
              <path
                d="M1845.47 670.99L1849.69 761.33L1840.83 761.74L1837 679.69L1818.8 680.54L1818.41 672.25L1845.47 670.99Z"
                fill="#9D9D9C"
                id="path170"
              />
              <path
                d="M1927.33 734.41C1926.84 751.43 1913.51 763.23 1896.68 762.75C1880.27 762.28 1868.6 750.57 1868.12 734.49L1876.98 734.74C1877.5 746.01 1885.51 753.84 1896.92 754.17C1908.87 754.51 1917.93 745.91 1918.28 733.87C1918.62 721.95 1909.44 712.88 1896.71 712.52L1887.42 712.26L1887.48 710.23L1911.36 679.4L1872.86 678.3L1873.1 670.01L1925.66 671.51L1925.59 673.82L1901.27 705.3C1917.09 707.02 1927.75 719.18 1927.32 734.42L1927.33 734.41Z"
                fill="#9D9D9C"
                id="path171"
              />
            </g>
            <g id="g172">
              <path
                d="M1775.59 486.54L1765.49 486.38L1765.17 505.93L1756.4 505.79L1756.72 486.24L1709.62 485.48L1709.66 483.09L1743.18 414.93L1752.56 415.08L1721.84 477.67L1756.93 478.24L1758.85 448.54L1766.1 448.66L1765.62 478.38L1775.72 478.54L1775.59 486.55V486.54Z"
                fill="#9D9D9C"
                id="path172"
              />
            </g>
            <g id="g174">
              <path
                d="M1509.84 212.14L1514.4 302.46L1505.54 302.91L1501.4 220.87L1483.2 221.79L1482.78 213.5L1509.83 212.13L1509.84 212.14Z"
                fill="#9D9D9C"
                id="path173"
              />
              <path
                d="M1583.48 254.15C1592.14 258.34 1597.5 267.21 1597.2 277.53C1596.75 293.46 1583.49 304.33 1564.87 303.8C1546.06 303.26 1533.72 291.94 1534.18 275.73C1534.47 265.59 1540.33 257.09 1549.33 253.18C1542.72 249.12 1538.65 241.47 1538.89 233.04C1539.29 219.13 1550.92 209.84 1567.54 210.31C1584.16 210.78 1595.15 220.63 1594.75 234.63C1594.51 243.2 1590.04 250.69 1583.48 254.15ZM1588.14 277.55C1588.47 265.9 1579.39 258.11 1566.19 257.73C1553.08 257.36 1543.57 264.61 1543.24 276.27C1542.92 287.42 1551.77 295.41 1565.11 295.79C1578.54 296.17 1587.83 288.71 1588.15 277.55H1588.14ZM1547.75 233.28C1547.47 243.06 1555.21 249.68 1566.4 250C1577.5 250.32 1585.6 244.14 1585.88 234.36C1586.15 225.03 1578.64 218.34 1567.31 218.01C1555.88 217.68 1548.01 223.94 1547.75 233.27V233.28Z"
                fill="#9D9D9C"
                id="path174"
              />
            </g>
            <g id="g175">
              <path
                d="M1293.97 81.72L1292.5 172.15L1283.63 172.01L1284.96 89.88L1266.74 89.58L1266.87 81.29L1293.96 81.73L1293.97 81.72Z"
                fill="#9D9D9C"
                id="path175"
              />
            </g>
            <g id="g177">
              <path
                d="M988.3 118.12L988.77 126.69L928.82 129.97L928.64 126.74C968.5 80.8598 972.83 72.8098 972.18 60.9598C971.63 50.9098 964.25 44.4998 953.67 45.0798C942.77 45.6798 935.79 53.0498 936.33 63.0098L936.46 65.2998L927.51 65.7898L927.38 63.4998C926.54 48.1298 937.19 37.3798 953.2 36.5098C968.97 35.6498 980.4 45.4698 981.22 60.4598C981.92 73.3398 977.66 82.8298 945.82 120.45L988.3 118.13V118.12Z"
                fill="#9D9D9C"
                id="path176"
              />
              <path
                d="M1001.71 81.1899C1003.03 52.3799 1017.07 35.1199 1038.31 36.0999C1059.46 37.0699 1071.86 55.5299 1070.54 84.3499C1069.22 113.09 1055.08 130.51 1034.03 129.54C1012.88 128.57 1000.39 109.93 1001.71 81.1999V81.1899ZM1061.49 83.9299C1062.58 60.1599 1053.56 45.3899 1037.92 44.6699C1022.18 43.9499 1011.85 57.8399 1010.76 81.5999C1009.67 105.41 1018.6 120.23 1034.42 120.96C1050.05 121.68 1060.4 107.73 1061.49 83.9299Z"
                fill="#9D9D9C"
                id="path177"
              />
            </g>
            <g id="g178">
              <path
                d="M953.84 905.6L620.08 250.55C618.59 251.3 617.11 252.06 615.62 252.82L949.38 907.87C950.84 907.08 952.33 906.32 953.83 905.6H953.84Z"
                fill="#9D9D9C"
                id="path178"
              />
            </g>
            <g id="g179">
              <path
                d="M1044.72 1094.96L1378.48 1750C1379.97 1749.25 1381.45 1748.49 1382.94 1747.73L1049.18 1092.68C1047.72 1093.47 1046.23 1094.23 1044.73 1094.95L1044.72 1094.96Z"
                fill="#9D9D9C"
                id="path179"
              />
            </g>
            <g id="g180">
              <path
                d="M980.39 1103.58L865.39 1829.65C867.04 1829.91 868.68 1830.18 870.33 1830.44L985.33 1104.36C983.67 1104.14 982.02 1103.88 980.39 1103.59V1103.58Z"
                fill="#9D9D9C"
                id="path180"
              />
            </g>
            <g id="g181">
              <path
                d="M1018.17 896.98L1133.17 170.91C1131.52 170.65 1129.88 170.38 1128.23 170.12L1013.23 896.2C1014.89 896.42 1016.54 896.68 1018.17 896.97V896.98Z"
                fill="#9D9D9C"
                id="path181"
              />
            </g>
            <g id="g182">
              <path
                d="M949.39 1092.69L615.63 1747.74C617.11 1748.5 618.6 1749.25 620.09 1750.01L953.85 1094.97C952.35 1094.25 950.86 1093.49 949.4 1092.7L949.39 1092.69Z"
                fill="#9D9D9C"
                id="path182"
              />
            </g>
            <g id="g183">
              <path
                d="M1049.17 907.87L1382.93 252.82C1381.45 252.06 1379.96 251.31 1378.47 250.55L1044.71 905.59C1046.21 906.31 1047.7 907.07 1049.16 907.86L1049.17 907.87Z"
                fill="#9D9D9C"
                id="path183"
              />
            </g>
            <g id="g184">
              <path
                d="M923.29 1072.74L403.55 1592.47C404.72 1593.65 405.9 1594.83 407.09 1596.01L926.82 1076.28C925.61 1075.13 924.44 1073.95 923.28 1072.74H923.29Z"
                fill="#9D9D9C"
                id="path184"
              />
            </g>
            <g id="g185">
              <path
                d="M1075.27 927.82L1595.01 408.09C1593.84 406.91 1592.66 405.73 1591.47 404.55L1071.74 924.28C1072.95 925.43 1074.12 926.61 1075.28 927.82H1075.27Z"
                fill="#9D9D9C"
                id="path185"
              />
            </g>
            <g id="g186">
              <path
                d="M1093.96 954.84L1749 621.08C1748.25 619.59 1747.49 618.11 1746.73 616.62L1091.68 950.38C1092.47 951.84 1093.23 953.33 1093.95 954.83L1093.96 954.84Z"
                fill="#9D9D9C"
                id="path186"
              />
            </g>
            <g id="g187">
              <path
                d="M904.6 1045.72L249.55 1379.48C250.3 1380.97 251.06 1382.45 251.82 1383.94L906.87 1050.18C906.08 1048.72 905.32 1047.23 904.6 1045.73V1045.72Z"
                fill="#9D9D9C"
                id="path187"
              />
            </g>
            <g id="g188">
              <path
                d="M1103.36 986.33L1829.44 871.33C1829.19 869.68 1828.92 868.04 1828.65 866.39L1102.58 981.39C1102.88 983.02 1103.13 984.67 1103.35 986.33H1103.36Z"
                fill="#9D9D9C"
                id="path188"
              />
            </g>
            <g id="g189">
              <path
                d="M895.2 1014.23L169.12 1129.23C169.37 1130.88 169.64 1132.52 169.91 1134.17L895.98 1019.17C895.68 1017.54 895.43 1015.89 895.21 1014.23H895.2Z"
                fill="#9D9D9C"
                id="path189"
              />
            </g>
            <g id="g190">
              <path
                d="M1103.36 1014.23C1103.14 1015.89 1102.88 1017.54 1102.59 1019.17L1828.66 1134.17C1828.92 1132.52 1829.19 1130.88 1829.45 1129.23L1103.37 1014.23H1103.36Z"
                fill="#9D9D9C"
                id="path190"
              />
            </g>
            <g id="g191">
              <path
                d="M895.98 981.39L169.91 866.39C169.65 868.04 169.38 869.68 169.12 871.33L895.2 986.33C895.42 984.67 895.68 983.02 895.97 981.39H895.98Z"
                fill="#9D9D9C"
                id="path191"
              />
            </g>
            <g id="g192">
              <path
                d="M1091.69 1050.17L1746.74 1383.93C1747.5 1382.45 1748.25 1380.96 1749.01 1379.47L1093.97 1045.71C1093.25 1047.21 1092.49 1048.7 1091.7 1050.16L1091.69 1050.17Z"
                fill="#9D9D9C"
                id="path192"
              />
            </g>
            <g id="g193">
              <path
                d="M906.87 950.39L251.82 616.63C251.06 618.11 250.31 619.6 249.55 621.09L904.59 954.85C905.31 953.35 906.07 951.86 906.86 950.4L906.87 950.39Z"
                fill="#9D9D9C"
                id="path193"
              />
            </g>
            <g id="g194">
              <path
                d="M1071.74 1076.27L1591.47 1596C1592.65 1594.83 1593.83 1593.65 1595.01 1592.46L1075.28 1072.73C1074.13 1073.94 1072.95 1075.12 1071.74 1076.27Z"
                fill="#9D9D9C"
                id="path194"
              />
            </g>
            <g id="g195">
              <path
                d="M926.82 924.29L407.09 404.55C405.91 405.72 404.73 406.9 403.55 408.09L923.28 927.82C924.43 926.61 925.61 925.44 926.82 924.28V924.29Z"
                fill="#9D9D9C"
                id="path195"
              />
            </g>
            <g id="g196">
              <path
                d="M985.33 896.2L870.33 170.12C868.68 170.37 867.04 170.64 865.39 170.91L980.39 896.98C982.02 896.68 983.67 896.43 985.33 896.21V896.2Z"
                fill="#9D9D9C"
                id="path196"
              />
            </g>
            <g id="g197">
              <path
                d="M1013.23 1104.36L1128.23 1830.44C1129.88 1830.19 1131.52 1829.92 1133.17 1829.65L1018.17 1103.58C1016.54 1103.88 1014.89 1104.13 1013.23 1104.35V1104.36Z"
                fill="#9D9D9C"
                id="path197"
              />
            </g>
          </svg>

          <div class="grid grid-cols-10 grid-rows-2 gap-2 text-white text-xl font-bold">
            <div class="white-border-wrapper rounded-xl col-span-10 h-9">
              <button class="bg-accent rounded-xl w-full h-full" @click="applyScore(0, $event)">
                Miss
              </button>
            </div>

            <div class="primary-border-wrapper col-span-5 rounded-xl h-9">
              <button
                @click="undo"
                :disabled="history.length === 0"
                class="bg-primary-light rounded-xl w-full h-full"
              >
                Undo
              </button>
            </div>

            <div class="primary-border-wrapper col-span-5 rounded-xl h-9">
              <button @click="resetConfirm" class="rounded-xl bg-primary-light w-full h-full">
                Reset
              </button>
            </div>
          </div>
        </section>

        <transition name="popup-fade">
          <div
            v-if="popupConfirmVisible"
            class="fixed inset-0 flex items-center justify-center bg-[rgba(1,36,94,0.75)] z-50"
          >
            <div
              class="flex w-full mx-4 p-8 items-start justify-center z-50 rounded-xl p-[3px]! gold-border-wrapper relative"
            >
              <div
                class="border-b-1 border-white pb-4 text-white rounded-xl px-6 py-2 w-full text-center bg-primary flex flex-col justify-start items-center gap-8"
              >
                <div class="flex w-full items-center justify-center">
                  <h3 class="mb-4 font-bold text-xl px-4 m-auto text-center">
                    Are you sure you want to reset mate?
                  </h3>
                </div>

                <div class="flex justify-center w-full gap-2 text-white text-xl font-bold">
                  <div class="white-border-wrapper rounded-xl col-span-10 h-9">
                    <button @click="resetGame" class="bg-accent rounded-xl w-full h-full px-4">
                      Yeah
                    </button>
                  </div>

                  <div class="primary-border-wrapper col-span-5 rounded-xl h-9">
                    <button
                      @click="popupConfirmVisible = false"
                      class="rounded-xl bg-primary-light w-full h-full px-4"
                    >
                      Nah
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <section class="bg-primary-mid py-8 px-2 rounded-b-xl" v-if="tab === 'keypad'">
          <div class="grid grid-cols-10 grid-rows-8 gap-2 text-white text-xl font-bold">
            <div
              class="white-border-wrapper flex items-center justify-center rounded-xl p-[1px] col-span-10"
            >
              <button class="bg-accent rounded-xl w-full h-full" @click="applyScore(0, $event)">
                Miss
              </button>
            </div>
            <button
              class="rounded-xl col-span-5"
              @click="selectedMultiplier = selectedMultiplier === 2 ? 1 : 2"
              :class="selectedMultiplier === 2 ? 'bg-green-dark text-green' : 'bg-green text-white'"
            >
              Double
            </button>
            <button
              class="rounded-xl col-span-5"
              @click="selectedMultiplier = selectedMultiplier === 3 ? 1 : 3"
              :class="selectedMultiplier === 3 ? 'bg-green-dark text-green' : 'bg-green text-white'"
            >
              Treble
            </button>
            <div
              class="flex items-center justify-center rounded-xl p-[1px] primary-border-wrapper col-span-5"
            >
              <button
                class="bg-primary-dark rounded-xl w-full h-full"
                @click="applyScore(25, $event)"
              >
                Outer Bull
              </button>
            </div>
            <div
              class="flex items-center justify-center rounded-xl p-[1px] primary-border-wrapper col-span-5"
            >
              <button
                class="bg-primary-dark rounded-xl w-full h-full"
                @click="applyScore(50, $event)"
              >
                Bullseye
              </button>
            </div>

            <div
              v-for="n in numbers"
              :key="n"
              :class="[
                'flex items-center justify-center rounded-xl p-[1px] primary-border-wrapper text-center col-span-2 transition-colors duration-200',
                pressedNumber === n
                  ? (idx % 2 === 0 ? 'bg-[#D388CA]' : 'bg-[#EA96A2]') + ' text-white'
                  : 'bg-primary-dark text-white',
              ]"
            >
              <button
                :class="[
                  'bg-primary-dark rounded-xl w-full h-full px-4 py-2 ',
                  pressedNumber === n
                    ? (idx % 2 === 0 ? 'bg-[#D388CA]' : 'bg-[#EA96A2]') + ' text-white'
                    : 'bg-primary-dark text-white',
                ]"
                @click="pressNumber(n, $event)"
              >
                {{ n }}
              </button>
            </div>
            <button
              @click="undo"
              :disabled="history.length === 0"
              class="bg-primary-light col-span-5 rounded-xl"
            >
              Undo
            </button>
            <button @click="resetConfirm" class="bg-primary-light col-span-5 rounded-xl">
              Reset
            </button>
          </div>
        </section>
        <SegmentPopup :popupType="popupType" :visible="popupVisible" />
      </div>

      <div class="flex w-full justify-center">
        <p class="text-base">Leg {{ currentLeg }}</p>
      </div>

      <PlayerScores
        :players="players"
        :currentPlayerIndex="currentPlayerIndex"
        :turnScores="turnScores"
      />
    </div>

    <div
      v-if="legEnded"
      class="fixed inset-0 flex items-center justify-center bg-[rgba(1,36,94,0.75)] z-50"
    >
      <div
        class="flex w-full mx-4 p-8 items-start justify-center z-50 rounded-xl p-[3px]! gold-border-wrapper relative"
        aria-hidden="false"
      >
        <div
          class="leg-popup border-b-1 border-white pb-4 text-white rounded-xl px-6 py-2 w-full text-center bg-primary flex flex-col justify-start items-center gap-8"
        >
          <div class="flex w-full mt-2">
            <div class="starting-score-banner">{{ startingScore }}</div>
            <button class="close-win" @click="closePopup()"></button>
          </div>

          <p class="text-base">Leg {{ currentLeg }} of {{ numberOfLegs }}</p>
          <div v-if="!gameEnded" class="flex flex-col gap-0 mont">
            <div class="text-3xl font-bold text-white uppercase leading-none text-shadow-lg">
              {{ winner }}
            </div>
            <div class="text-5xl uppercase font-extrabold leading-none bubbles-text-background">
              WINS
            </div>
            <div class="text-3xl font-bold text-white uppercase leading-none text-shadow-lg">
              this leg
            </div>
          </div>
          <div v-if="gameEnded && gameWinners.length === 1" class="flex flex-col gap-0 mont">
            <div class="text-3xl font-bold text-white uppercase leading-none text-shadow-lg">
              {{ gameWinners[0].name }}
            </div>
            <div class="text-5xl uppercase font-extrabold leading-none bubbles-text-background">
              WINS
            </div>
            <div
              class="text-3xl font-bold bubbles-text-background uppercase leading-none text-shadow-lg"
            >
              the game
            </div>
          </div>
          <div v-if="gameEnded && gameWinners.length > 1" class="flex flex-col gap-0 mont">
            <div class="text-3xl font-bold text-white uppercase leading-none text-shadow-lg">
              Fetch a pencil
            </div>
            <div class="text-3xl font-bold text-white uppercase leading-none text-shadow-lg">
              because it's a
            </div>
            <div class="text-5xl font-bold text-accent uppercase leading-none text-shadow-lg">
              draw
            </div>
          </div>

          <p class="text-base border-b-1 border-white pb-4 w-full">LEG: {{ currentLeg }}</p>
          <PlayerLegStats :players="players" :history="history" :leg-duration="legDuration" />

          <div class="flex flex-col gap-2 w-full" v-if="!gameEnded">
            <div class="white-border-wrapper rounded-xl w-full">
              <button class="bg-accent rounded-xl w-full h-9 font-bold" @click="startNewLeg()">
                Continue
              </button>
            </div>

            <div class="primary-border-wrapper rounded-xl w-full">
              <button
                class="bg-primary-light rounded-xl w-full h-9 font-bold"
                @click="startGame(true)"
              >
                Rematch
              </button>
            </div>
          </div>
          <div class="flex flex-col gap-2 w-full" v-if="gameEnded">
            <div class="white-border-wrapper rounded-xl w-full">
              <button class="bg-accent rounded-xl w-full h-9 font-bold" @click="startGame(true)">
                Rematch
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue';
import AccentButton from './AccentButton.vue';
import SegmentPopup from './SegmentPopup.vue';
import ButtonToggle from './ButtonToggle.vue';
import NumberNudger from './NumberNudger.vue';
import PlayerScores from './PlayerScores.vue';
import PlayerLegStats from './PlayerLegStats.vue';
//import { VueZoomable } from 'vue-zoomable';
import 'vue-zoomable/dist/style.css';
import { gtmEvent } from '../utils/analytics';

export default {
  name: 'DartsCalculator',
  components: {
    AccentButton,
    SegmentPopup,
    ButtonToggle,
    NumberNudger,
    PlayerScores,
    PlayerLegStats,
    //VueZoomable,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const playerNames = ref(['', '', '', '', '', '', '', '']);
    const startingScore = ref(501);
    const numberOfPlayers = ref(2);
    const numberOfLegs = ref(1);
    const started = ref(false);
    const tab = ref('board');
    const popupVisible = ref(false);
    const popupConfirmVisible = ref(false);
    const popupType = ref('');
    const currentLeg = ref(1);
    const players = ref([{}, {}, {}, {}, {}, {}, {}, {}]);
    const gameOut = ref(1);

    // Track per-player scores for current turn (3 throws)
    const turnScores = ref([
      [null, null, null],
      [null, null, null],
      [null, null, null],
      [null, null, null],
      [null, null, null],
      [null, null, null],
      [null, null, null],
      [null, null, null],
    ]);

    // keep history of moves for undo
    const history = ref([]);

    const currentPlayerIndex = ref(0);
    const throwsRemaining = ref(3);
    const numbers = Array.from({ length: 20 }, (_, i) => i + 1);
    const multipliers = [
      { label: 'Single', value: 1 },
      { label: 'Double', value: 2 },
      { label: 'Treble', value: 3 },
    ];
    const selectedMultiplier = ref(1);
    const winner = ref('');
    const gameWinners = computed(() => {
      if (!gameEnded.value) {
        return [];
      }

      const maxLegs = Math.max.apply(
        Math,
        players.value.map((p) => p.legScore)
      );

      //console.log(maxLegs);
      return players.value.filter((p) => p.legScore === maxLegs);
    });

    const lastError = ref('');
    const gameEnded = ref(false);
    const legEnded = ref(false);
    const currentPlayerScore = ref(0);
    const legStartTime = ref(null);

    const currentPlayerName = computed(() => players[currentPlayerIndex.value].name);

    const legDuration = computed(() => {
      if (!legStartTime.value) return '0:00';

      const endTime = legEnded.value ? legStartTime.value.endTime : Date.now();
      const durationMs = endTime - legStartTime.value.startTime;
      const totalSeconds = Math.floor(durationMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    });

    const gameDescriptions = {
      301: 'No messing – the fast-paced option. Start on 301 and subtract each throw from your total. Finish bang on zero by checking out with a double. Cheeky celebration dance optional. ',
      501: 'The classic longer leg. Perfect for getting your eye in before that final double. If it’s good enough for the pros...',
      701: 'For the big sessions. More darts, more drama, and an even sweeter checkout when you hit that winning double.',
    };
    // transient pressed number highlight state
    const pressedNumber = ref(null);
    let _pressTimeout = null;
    // transient pressed segment highlight timeout
    let _segmentTimeout = null;
    let _buttonTimeout = null;

    function showPopup(type) {
      popupType.value = type;
      popupVisible.value = true;

      window.setTimeout(() => {
        popupVisible.value = false;
      }, 2000);
    }

    function startNewLeg() {
      gtmEvent('start_new_leg');

      currentLeg.value = currentLeg.value + 1;

      for (var i = 0; i < numberOfPlayers.value; i++) {
        players.value[i].score = startingScore.value;
      }

      currentPlayerIndex.value = 0;
      throwsRemaining.value = 3;
      history.value.splice(0, history.value.length);
      winner.value = '';
      legEnded.value = false;
      started.value = true;
      legStartTime.value = { startTime: Date.now(), endTime: null };

      for (var i = 0; i < 8; i++) {
        turnScores.value[i] = [null, null, null];
      }
    }

    function switchTab(selectedTab) {
      tab.value = selectedTab;
    }

    function startGame(rematch = false) {
      let isRematch = 'No';

      if (rematch !== undefined) {
        isRematch = rematch ? 'Yes' : 'No';
      }

      gtmEvent('game_started', {
        players: numberOfPlayers.value,
        startingScore: startingScore.value,
        endGame: gameOut.value == 1 ? 'Double Out' : 'Dead Easy Out',
        numberOfLegs: numberOfLegs.value,
        rematch: isRematch,
      });

      players.value = Array.from(Array(numberOfPlayers.value).keys()).map((x) => {
        return {
          name: playerNames.value[x] || 'Player ' + (x + 1),
          score: startingScore.value,
          legScore: 0,
        };
      });

      currentPlayerIndex.value = 0;
      throwsRemaining.value = 3;
      history.value.splice(0, history.value.length);
      winner.value = '';
      legEnded.value = false;
      gameEnded.value = false;
      started.value = true;
      currentLeg.value = 1;
      legStartTime.value = { startTime: Date.now(), endTime: null };

      for (var i = 0; i < 8; i++) {
        turnScores.value[i] = [null, null, null];
      }

      headerElements(true);

      const dashboard = document.getElementById('play-dashboard');
      if (dashboard) {
        const yOffset = -10;
        const y = dashboard.getBoundingClientRect().top + window.scrollY + yOffset;
        setTimeout(() => {
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 500);
      }
    }

    function headerElements(hide) {
      const headerImage = document.getElementById('header-banner');
      if (headerImage) {
        if (hide) {
          headerImage.style.display = 'none';
        } else {
          headerImage.style.display = '';
        }
      }

      const headerText = document.getElementById('header-introduction');
      if (headerText) {
        if (hide) {
          headerText.style.display = 'none';
        } else {
          headerText.style.display = '';
        }
      }
    }

    function closePopup() {
      if (!gameEnded.value) {
        startNewLeg();
      } else {
        resetGame();
      }
    }

    function checkPopups(value, newScore) {
      const lastDart = throwsRemaining.value === 1;

      const total = currentPlayerScore.value;

      if (lastDart) {
        if (total === 180) {
          showPopup('oneHundredAndEighty');
          return;
        } else if (total === 170 && value === 50 && newScore === 0) {
          // only showing this if you finish with T20, T20, Bullseye
          showPopup('theBigFish');
          return;
        } else if (
          total === 26 &&
          turnScores.value[currentPlayerIndex.value].indexOf('20') > -1 &&
          turnScores.value[currentPlayerIndex.value].indexOf('1') > -1 &&
          turnScores.value[currentPlayerIndex.value].indexOf('5') > -1
        ) {
          showPopup('classic26');
          return;
        }
      }

      if (value === 50) {
        showPopup('bullseye');
        return;
      }
      if (value === 20 && selectedMultiplier.value === 3) {
        showPopup('treble20');
        return;
      }
      if (value === 20 && selectedMultiplier.value === 2) {
        showPopup('doubleTop');
        return;
      }
      if (value === 3 && selectedMultiplier.value === 2 && newScore > 0) {
        showPopup('downUnderDouble');
        return;
      }
      if (value === 3 && selectedMultiplier.value === 2 && newScore === 0) {
        showPopup('downUnderCheckout');
        return;
      }
    }

    function applyScore(value, event) {
      gtmEvent('score_tracking', {
        method: tab.value == 'board' ? 'Dartboard' : 'Keypad',
      });

      if (winner.value) return;
      lastError.value = '';

      if (value === 0) {
        if (event != null) {
          try {
            const el = event.target;
            el.style.backgroundColor = '#e95e73';
            _buttonTimeout = setTimeout(() => {
              el.style.backgroundColor = '#e22844';
              _buttonTimeout = null;
            }, 1000);
          } catch (e) {
            // ignore if DOM manipulation fails
          }
        }
      }

      if (value === 25 || value === 50) {
        selectedMultiplier.value = 1;

        if (event != null) {
          try {
            const el = event.target;
            el.style.backgroundColor = '#00368c';
            _buttonTimeout = setTimeout(() => {
              el.style.backgroundColor = '#01112f';
              _buttonTimeout = null;
            }, 1000);
          } catch (e) {
            // ignore if DOM manipulation fails
          }
        }
      }

      const p = players.value[currentPlayerIndex.value];
      const prevScore = p.score;
      const scoredValue = value * selectedMultiplier.value;
      // compute which slot (0..2) this throw should occupy for the current player
      const slotIndex = 3 - throwsRemaining.value;
      // label to display for this throw (e.g., D20, T20, 19)
      let displayLabel;
      if (selectedMultiplier.value === 2) {
        displayLabel = `D${value}`;
      } else if (selectedMultiplier.value === 3) {
        displayLabel = `T${value}`;
      } else {
        displayLabel = `${value}`;
      }
      const newScore = prevScore - scoredValue;

      // Check if score would go negative or equal to 1
      if (newScore < 0 || newScore === 1) {
        showPopup('busted');
        switchPlayer();
        return;
      }

      currentPlayerScore.value += scoredValue;
      // Check exact finish: must be 0 and must be on a double
      if (newScore === 0) {
        if (selectedMultiplier.value !== 2 && value !== 50 && gameOut.value == 1) {
          showPopup('busted');
          switchPlayer();
          return;
        }
        // Valid finish on a double
        p.score = 0;
        history.value.push({
          player: currentPlayerIndex.value,
          value,
          multiplier: selectedMultiplier.value,
          prevScore,
          scoredValue,
        });
        // record the display label into the turn display
        if (slotIndex >= 0 && slotIndex <= 2) {
          turnScores.value[currentPlayerIndex.value][slotIndex] = displayLabel;
          checkPopups(value, newScore);
        }

        p.legScore = p.legScore + 1;
        winner.value = p.name;

        legEnded.value = true;

        gtmEvent('leg_ended');

        if (legStartTime.value) {
          legStartTime.value.endTime = Date.now();
        }

        const endTime = legEnded.value ? legStartTime.value.endTime : Date.now();
        const durationMs = endTime - legStartTime.value.startTime;
        const totalSeconds = Math.floor(durationMs / 1000);

        if (currentLeg.value === numberOfLegs.value) {
          gameEnded.value = true;
          gtmEvent('game_ended', {
            duration: totalSeconds,
          });
        }

        return;
      }

      // Normal score application
      p.score = newScore;
      history.value.push({
        player: currentPlayerIndex.value,
        value,
        multiplier: selectedMultiplier.value,
        prevScore,
        scoredValue,
      });

      // record the display label into the turn display
      if (slotIndex >= 0 && slotIndex <= 2) {
        turnScores.value[currentPlayerIndex.value][slotIndex] = displayLabel;
      }

      checkPopups(value, newScore);

      // decrement throws and maybe switch
      throwsRemaining.value -= 1;
      if (throwsRemaining.value <= 0) {
        switchPlayer();
      }

      selectedMultiplier.value = 1; // reset to single after each throw
    }

    function switchPlayer() {
      currentPlayerIndex.value = (currentPlayerIndex.value + 1) % numberOfPlayers.value;
      throwsRemaining.value = 3;
      // reset the turn scores for the player whose new turn is starting
      turnScores.value[currentPlayerIndex.value] = [null, null, null];
      currentPlayerScore.value = 0;
    }

    function undo() {
      if (history.value.length === 0) return;
      const last = history.value.pop();
      const p = players.value[last.player];
      p.score = last.prevScore;

      // restore turn ownership and throws remaining accordingly
      currentPlayerIndex.value = last.player;

      // Scan history to count how many moves the current player has made in the current turn
      let count = 0;
      for (let i = history.value.length - 1; i >= 0; i--) {
        if (history.value[i].player === currentPlayerIndex.value) count += 1;
        else break;
      }

      // current player's used throws in this turn is count
      throwsRemaining.value = 3 - count;
      if (throwsRemaining.value <= 0) throwsRemaining.value = 1; // at least 1

      // Remove the entry from turnScores for the current player
      // The slot index for the undone throw is based on how many throws were remaining before the undo
      const slotIndex = 3 - throwsRemaining.value;
      if (slotIndex >= 0 && slotIndex <= 2) {
        turnScores.value[currentPlayerIndex.value][slotIndex] = null;
      }

      // Recalculate currentPlayerScore by summing up the current turn's scores from history
      currentPlayerScore.value = 0;
      for (let i = history.value.length - 1; i >= 0; i--) {
        if (history.value[i].player === currentPlayerIndex.value) {
          currentPlayerScore.value += history.value[i].scoredValue;
        } else {
          break;
        }
      }

      winner.value = '';
      lastError.value = '';
    }

    function resetConfirm() {
      popupConfirmVisible.value = true;
    }

    function resetGame(showPopup = false) {
      gtmEvent('reset_game');
      popupConfirmVisible.value = false;
      started.value = false;
      gameEnded.value = false;
      legEnded.value = false;
      headerElements(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function handleSegmentClick(event) {
      const segment = event.target.dataset.segment;
      if (!segment) return;

      // Parse segment value and multiplier
      // Examples: "D10" -> value=10, multiplier=2 (double)
      //          "T20" -> value=20, multiplier=3 (triple)
      //          "19"  -> value=19, multiplier=1 (single)
      let value;
      let multiplier = 1;

      if (segment.startsWith('D')) {
        value = parseInt(segment.substring(1));
        multiplier = 2;
      } else if (segment.startsWith('T')) {
        value = parseInt(segment.substring(1));
        multiplier = 3;
      } else {
        value = parseInt(segment);
      }

      if (isNaN(value)) return;
      selectedMultiplier.value = multiplier;

      try {
        const el = event.target;
        el.classList.add('pressed-seg');
        _segmentTimeout = setTimeout(() => {
          el.classList.remove('pressed-seg');
          _segmentTimeout = null;
        }, 1000);
      } catch (e) {
        // ignore if DOM manipulation fails
      }

      applyScore(value, null);
    }

    function pressNumber(n, event) {
      if (winner.value) return;
      // set transient highlight
      pressedNumber.value = n;

      try {
        const el = event.target;
        el.style.backgroundColor = '#00368c';
        _buttonTimeout = setTimeout(() => {
          el.style.backgroundColor = '#01112f';
          _buttonTimeout = null;
        }, 1000);
      } catch (e) {
        // ignore if DOM manipulation fails
      }

      // clear previous timeout if any
      if (_pressTimeout) {
        clearTimeout(_pressTimeout);
        _pressTimeout = null;
      }
      _pressTimeout = setTimeout(() => {
        pressedNumber.value = null;
        _pressTimeout = null;
      }, 1000);

      // Apply the score (respecting selectedMultiplier)
      applyScore(n, null);
    }

    return {
      playerNames,
      startingScore,
      started,
      players,
      numbers,
      multipliers,
      selectedMultiplier,
      currentPlayerIndex,
      throwsRemaining,
      applyScore,
      undo,
      resetGame,
      startGame,
      history,
      currentPlayerName,
      winner,
      gameWinners,
      lastError,
      turnScores,
      pressedNumber,
      pressNumber,
      handleSegmentClick,
      tab,
      popupVisible,
      popupType,
      numberOfPlayers,
      numberOfLegs,
      currentLeg,
      startNewLeg,
      gameEnded,
      legEnded,
      legStartTime,
      legDuration,
      gameDescriptions,
      gameOut,
      closePopup,
      headerElements,
      resetConfirm,
      popupConfirmVisible,
      switchTab,
    };
  },
};
</script>

<style scoped>
.controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;
}

.score-buttons {
  display: flex;
  gap: 0.75rem;
}

.score-btn {
  padding: 1.2rem 3rem;
  border-radius: 16px;
  background-color: var(--color-primary-mid);
  cursor: pointer;
  transition: all 0.2s ease;
}

.score-btn.active {
  background: linear-gradient(270deg, #f2c308 0%, #d9ac33 29.5%, #d9ac33 69.5%, #eed67c 100%);
  color: #fff;
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.multiplier-selector {
  display: flex;
  gap: 1rem;
  margin: 1rem 0;
  align-items: center;
}

.multiplier-selector label {
  font-weight: 600;
}

.mult-btn {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  border-radius: 6px;
  border: 2px solid #999;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mult-btn.active {
  border-color: #007bff;
  background: #007bff;
  color: #fff;
  font-weight: 600;
}

.mult-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.player-card {
  flex: 1;
  border: 1px solid #ccc;
  padding: 0.5rem;
  border-radius: 6px;
  text-align: center;
}

/* Highlight style applied to pressed SVG path */
.pressed-seg {
  fill: #00368c;
  transition: fill 0.2s ease;
}

.player-card.active {
  border-color: #007bff;
  background: #000;
}

.numbers {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
  max-width: 480px;
}

.number-btn {
  padding: 0.6rem;
  font-size: 1rem;
  border-radius: 6px;
  border: 1px solid #888;
  cursor: pointer;
}

.number-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.actions {
  margin-top: 1rem;
  display: flex;
  gap: 0.5rem;
}

.winner {
  color: green;
  font-weight: 700;
}

.error {
  color: #d32f2f;
  font-weight: 600;
}

.status {
  text-align: right;
  font-size: 0.9rem;
}

.leg-popup {
  background-image: url('../assets/confetti.gif');
  background-size: cover;
}

.close-win,
.back-btn {
  position: absolute;
  width: 41px;
  height: 41px;
  background-size: 41px;
  background-size: contain;
  background-repeat: no-repeat;
}

.close-win {
  right: 1rem;
  background-image: url('data:image/svg+xml,<svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="21" cy="21" r="20.5" fill="url(%23paint0_linear_1949_9675)" stroke="url(%23paint1_linear_1949_9675)"/><path fill-rule="evenodd" clip-rule="evenodd" d="M21.0091 23.1536L26.3973 28.5357C26.6833 28.8213 27.0711 28.9818 27.4754 28.9818C27.8797 28.9818 28.2675 28.8213 28.5535 28.5357C28.8394 28.2501 29 27.8628 29 27.4589C29 27.055 28.8394 26.6677 28.5535 26.3821L23.1632 21L28.5524 15.6179C28.6939 15.4765 28.8062 15.3086 28.8827 15.1239C28.9593 14.9392 28.9987 14.7412 28.9986 14.5412C28.9986 14.3413 28.9591 14.1433 28.8825 13.9586C28.8058 13.7739 28.6935 13.6061 28.5519 13.4648C28.4104 13.3234 28.2423 13.2113 28.0574 13.1348C27.8724 13.0584 27.6742 13.019 27.474 13.0191C27.2739 13.0191 27.0757 13.0586 26.8908 13.1351C26.7058 13.2117 26.5378 13.3239 26.3963 13.4653L21.0091 18.8474L15.6209 13.4653C15.4804 13.3198 15.3122 13.2037 15.1263 13.1239C14.9404 13.044 14.7404 13.0019 14.5381 13.0001C14.3357 12.9982 14.135 13.0366 13.9476 13.1131C13.7603 13.1896 13.5901 13.3025 13.4469 13.4454C13.3037 13.5883 13.1905 13.7582 13.1138 13.9452C13.037 14.1323 12.9984 14.3327 13.0001 14.5349C13.0017 14.737 13.0437 14.9368 13.1235 15.1226C13.2032 15.3083 13.3193 15.4764 13.4648 15.6169L18.855 21L13.4658 26.3831C13.3203 26.5236 13.2043 26.6917 13.1245 26.8774C13.0447 27.0632 13.0027 27.263 13.0011 27.4651C12.9994 27.6673 13.0381 27.8677 13.1148 28.0548C13.1915 28.2418 13.3048 28.4117 13.4479 28.5546C13.5911 28.6975 13.7613 28.8104 13.9487 28.8869C14.136 28.9634 14.3367 29.0018 14.5391 28.9999C14.7415 28.9981 14.9414 28.956 15.1273 28.8761C15.3132 28.7963 15.4814 28.6802 15.6219 28.5347L21.0091 23.1536Z" fill="white"/><defs><linearGradient id="paint0_linear_1949_9675" x1="-1.27126e-06" y1="-0.499999" x2="42" y2="41.5" gradientUnits="userSpaceOnUse"><stop stop-color="%23F2C308"/><stop offset="0.295" stop-color="%23D9AC33"/><stop offset="0.695" stop-color="%23D9AC33"/><stop offset="1" stop-color="%23EED67C"/></linearGradient><linearGradient id="paint1_linear_1949_9675" x1="0" y1="0" x2="45.2847" y2="3.91068" gradientUnits="userSpaceOnUse"><stop stop-color="white"/><stop offset="0.0480769" stop-color="white" stop-opacity="0.25"/><stop offset="0.1" stop-color="%23E9CD65"/><stop offset="0.9" stop-color="%23E9CD65"/><stop offset="0.95" stop-color="white" stop-opacity="0.25"/><stop offset="1" stop-color="white"/></linearGradient></defs></svg>');
}

.back-btn {
  left: 0;
  background-image: url('data:image/svg+xml,<svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="21" cy="21" r="20.5" fill="url(%23paint0_linear_1022_72631)" stroke="url(%23paint1_linear_1022_72631)"/><path d="M21 17.6903V15.0364C20.9997 14.8315 20.9372 14.6312 20.8204 14.4609C20.7037 14.2905 20.5379 14.1577 20.3439 14.0792C20.15 14.0007 19.9365 13.98 19.7305 14.0196C19.5245 14.0593 19.3352 14.1576 19.1863 14.3022L12.5 20.7404L19.1863 27.1776C19.2848 27.2742 19.4018 27.3509 19.5307 27.4033C19.6596 27.4556 19.7979 27.4825 19.9375 27.4825C20.0771 27.4825 20.2154 27.4556 20.3443 27.4033C20.4732 27.3509 20.5902 27.2742 20.6887 27.1776C20.888 26.9831 20.9999 26.7194 21 26.4444V23.8631C23.9219 23.9336 27.1147 24.4501 29.5 28V26.9629C29.5 22.1581 25.7812 18.2068 21 17.6903Z" fill="white"/><defs><linearGradient id="paint0_linear_1022_72631" x1="-1.27126e-06" y1="-0.499999" x2="42" y2="41.5" gradientUnits="userSpaceOnUse"><stop stop-color="%23F2C308"/><stop offset="0.295" stop-color="%23D9AC33"/><stop offset="0.695" stop-color="%23D9AC33"/><stop offset="1" stop-color="%23EED67C"/></linearGradient><linearGradient id="paint1_linear_1022_72631" x1="0" y1="0" x2="45.2847" y2="3.91068" gradientUnits="userSpaceOnUse"><stop stop-color="white"/><stop offset="0.0480769" stop-color="white" stop-opacity="0.25"/><stop offset="0.1" stop-color="%23E9CD65"/><stop offset="0.9" stop-color="%23E9CD65"/><stop offset="0.95" stop-color="white" stop-opacity="0.25"/><stop offset="1" stop-color="white"/></linearGradient></defs></svg>');
}

.starting-score-banner {
  border: 1px solid #fff;
  background: var(--color-primary-dark, #01112f);
  border-radius: 24px;
  line-height: 1.75rem;
  min-height: 42px;
  padding: 4px 21px 0 21px;
  margin: 0 auto;
  color: #fff;
  font-family: var(--font-regular, Faustina);
  font-size: 1.75rem;
  font-style: normal;
  font-weight: 800;
  text-align: center;
  min-width: 60%;
}

.header .heading {
  max-width: calc(100% - 82px);
}

.header .heading h3 {
  min-width: calc(100% - 1rem);
  line-height: 42px;
  padding: 0;
}
</style>
